// @section 1 OptiqHQ API client — thin wrapper over authFetch
// HQ endpoints live on the OptiqOS API at api.optiq-os.com.
// Workspace (gmail, calendar via WIF+DWD) is proxied through /api/hq/workspace/*
// so the browser only ever sends the MSAL idToken; the OptiqOS API carries the
// Bridge X-Api-Key server-side. Bearer token is injected by authFetch.

import { authFetch, getAccessToken } from './lib/authFetch';

const API_BASE =
  import.meta.env.VITE_HQ_API_BASE ?? 'https://api.optiq-os.com';

// @block 1.1 Fallback Workspace identity for Bridge calls (subject impersonation).
// Prefer the logged-in user's identity from GET /api/hq/me (see block 1.0b +
// lib/useMe). ADAM_SUBJECT remains only as a last-resort default if /me fails.
export const ADAM_SUBJECT = 'adam_caccamise@navipoint-technologies.com';

// @block 1.2 Current Atlas user — resolved server-side from the validated JWT.
// userId keys hq tasks/mentions; workspaceSubject is the Google Workspace DWD
// subject for calendar/gmail (== email). This is what makes every per-user
// surface (My Day, CRM, …) show the signed-in operator's data, not Adam's.
export type Me = {
  userId: string;
  email: string;
  displayName: string;
  timezone: string;
  workspaceSubject: string;
};

export const getMe = () => req<Me>('/api/hq/me');

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${API_BASE}${path}`, init);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HQ API ${res.status}: ${text || res.statusText}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// @block 1.3 Health
export const health = () => req<{ ok: boolean; surface: string }>('/api/hq/health');

// @block 1.4 CRM
export type Company = { id: string; name: string; domain?: string; industry?: string; employeeCount?: number; createdAt: string; updatedAt: string; isActiveClient?: boolean };
export type Contact = { id: string; companyId?: string; firstName?: string; lastName?: string; fullName?: string; email?: string; phone?: string; title?: string; tags: string[]; createdAt: string };
export type Deal = { id: string; name: string; companyId?: string; stage: string; valueCents?: number; probability?: number; expectedClose?: string; closedAt?: string };

export const listCompanies = (q?: string) => req<Company[]>(`/api/hq/crm/companies${q ? `?q=${encodeURIComponent(q)}` : ''}`);
export const createCompany = (body: { name: string; domain?: string; industry?: string }) =>
  req<Company>('/api/hq/crm/companies', { method: 'POST', body: JSON.stringify(body) });

export const listContacts = (q?: string) => req<Contact[]>(`/api/hq/crm/contacts${q ? `?q=${encodeURIComponent(q)}` : ''}`);
export const createContact = (body: { firstName?: string; lastName?: string; email?: string; companyId?: string }) =>
  req<Contact>('/api/hq/crm/contacts', { method: 'POST', body: JSON.stringify(body) });

export const listDeals = (stage?: string) => req<Deal[]>(`/api/hq/crm/deals${stage ? `?stage=${encodeURIComponent(stage)}` : ''}`);

// @block 1.5 Strategy
export type Vision = { id: string; title: string; body?: string; horizonYears: number; createdAt: string };
export type StrategyCascade = { vision: { id: string; title: string } | null; objectives: any[] };

export const getCascade = () => req<StrategyCascade>('/api/hq/strategy/cascade');
export const createVision = (body: { title: string; body?: string; horizonYears?: number }) =>
  req<Vision>('/api/hq/strategy/visions', { method: 'POST', body: JSON.stringify(body) });

// @block 1.6 PM
export type Task = { id: string; title: string; status: string; priority: number; parentType: string; parentId?: string; dueDate?: string };

export const listTasks = (params?: { status?: string; assigneeId?: string }) => {
  const qs = new URLSearchParams(params as any).toString();
  return req<Task[]>(`/api/hq/pm/tasks${qs ? `?${qs}` : ''}`);
};
export const createTask = (body: {
  title: string;
  parentType: string;
  parentId?: string;
  priority?: number;
  dueDate?: string;
  assigneeId?: string;
  body?: string;
}) => req<Task>('/api/hq/pm/tasks', { method: 'POST', body: JSON.stringify(body) });

// @block 1.7 Projects — Vertis-internal project tracker surface
export type Project = {
  id: string;
  name: string;
  anchorType?: string | null;
  anchorId?: string | null;
  status: string;            // proposed | active | paused | done | dropped
  startsOn?: string | null;
  endsOn?: string | null;
};

export const listProjects = () => req<Project[]>('/api/hq/pm/projects');
export const transitionTask = (id: string, status: string) =>
  req<Task>(`/api/hq/pm/tasks/${id}/transition`, { method: 'POST', body: JSON.stringify({ status }) });

// @block 1.8 ERP
export const getCash = () => req<{ rows: any[]; note?: string }>('/api/hq/erp/cash');
export const getRunway = () => req<any>('/api/hq/erp/runway');

// @block 1.9 My Day
export const getMyDay = (userId?: string) =>
  req<any>(`/api/hq/my-day${userId ? `?userId=${userId}` : ''}`);

// @block 1.10 Delivery
export const listEngagements = () => req<any[]>('/api/hq/delivery/engagements');
export const listDeliverables = (engagementId?: string) =>
  req<any[]>(`/api/hq/delivery/deliverables${engagementId ? `?engagementId=${engagementId}` : ''}`);
export const createShareLink = (deliverableId: string) =>
  req<{ id: string; token: string; url: string; expiresAt: string }>(`/api/hq/delivery/deliverables/${deliverableId}/share`, { method: 'POST' });

// @block 1.11 Morning metrics — site-wide website visitors (human vs scanner).
// Forward "website traffic" surface under the 2026-06-19 no-links cold-email doctrine:
// per-host daily visitors split human vs scanner, from analytics.ga4_site_daily_visitors.
export type Ga4SiteHost = { site_host: string; property_id: string; visitors: number; human: number; scanner: number };
export type Ga4SiteDay = { event_date: string; visitors: number; human: number; scanner: number };
export type Ga4SiteDaily = {
  latest_date: string | null;
  totals: { visitors: number; human: number; scanner: number };
  by_host: Ga4SiteHost[];
  daily: Ga4SiteDay[];
};
export const getGa4SiteDaily = (days?: number) =>
  req<Ga4SiteDaily>(`/api/hq/morning-metrics/ga4-daily-site-wide${days ? `?days=${days}` : ''}`);

// @section 2 Workspace helpers (calendar + gmail) — proxied via /api/hq/workspace/*
// (OptiqOS API validates the idToken, then forwards to Bridge WIF+DWD with the
// server-side X-Api-Key). Shapes are passed through unchanged.

// @block 2.1 Calendar — events between timeMin and timeMax (ISO 8601). Default: now → +24h.
export type CalEvent = {
  id: string;
  summary?: string;
  description?: string;
  location?: string;
  htmlLink?: string;
  hangoutLink?: string;
  start?: { dateTime?: string; date?: string; timeZone?: string };
  end?: { dateTime?: string; date?: string; timeZone?: string };
  attendees?: { email: string; displayName?: string; responseStatus?: string }[];
  organizer?: { email: string; displayName?: string };
  status?: string;
  transparency?: string; // "transparent" = Free/Available, else Busy
  eventType?: string;
  extendedProperties?: { private?: Record<string, string>; shared?: Record<string, string> };
  // Client-tagged in the unified My Day view: which Workspace account this event
  // came from. Unset for single-calendar callers.
  accountSubject?: string;
  accountLabel?: string;
};

export async function listCalendarEvents(
  subject: string,
  timeMin?: string,
  timeMax?: string,
  maxResults = 20,
): Promise<{ items: CalEvent[] }> {
  const params = new URLSearchParams({ subject, maxResults: String(maxResults) });
  if (timeMin) params.set('timeMin', timeMin);
  if (timeMax) params.set('timeMax', timeMax);
  return req<{ items: CalEvent[] }>(
    `/api/hq/workspace/calendar/events?${params}`,
  );
}

// @block 2.2 Unified calendars — the Workspace calendars My Day merges into one
// timeline. The server is authoritative (GET /api/hq/me/calendars); the
// ADAM_UNIFIED_FALLBACK below only kicks in if that endpoint isn't deployed yet,
// the same graceful-degradation pattern used by §3.3 / §4 / §5.
export type WorkspaceCalendar = { subject: string; label: string };

const ADAM_LOGINS = new Set([
  'adam_caccamise@navipoint-technologies.com',
  'system-admin@navipoint-technologies.com',
]);
const ADAM_UNIFIED_FALLBACK: WorkspaceCalendar[] = [
  { subject: 'adam_caccamise@navipoint-technologies.com', label: 'NaviPoint — Adam' },
  { subject: 'system-admin@navipoint-technologies.com', label: 'NaviPoint — System-Admin' },
  { subject: 'adam_caccamise@vertisholdings.com', label: 'Vertis Holdings — Adam' },
  { subject: 'system-admin@vertisholdings.com', label: 'Vertis Holdings — System-Admin' },
  { subject: 'adamc@conversiv.com', label: 'Conversiv — Adam' },
];

export async function listMyCalendars(me: Me): Promise<WorkspaceCalendar[]> {
  try {
    const r = await authFetch(`${API_BASE}/api/hq/me/calendars`);
    if (r.ok) {
      const j = await r.json();
      if (Array.isArray(j?.calendars) && j.calendars.length) return j.calendars as WorkspaceCalendar[];
    }
    // 404 (not deployed yet) / empty → fall through to the fallback
  } catch {
    // network/CORS → fallback
  }
  const self: WorkspaceCalendar = {
    subject: me.workspaceSubject,
    label: me.workspaceSubject.split('@')[0],
  };
  return ADAM_LOGINS.has((me.email || '').toLowerCase()) ? ADAM_UNIFIED_FALLBACK : [self];
}

// @block 2.3 Unified fetch — pull every calendar in parallel, tag each event
// with its source account, merge, de-dupe (the same meeting can sit on more than
// one of Adam's calendars), and sort by start. One account failing never blanks
// the whole timeline — its error is returned alongside what did load.
export async function listUnifiedCalendarEvents(
  cals: WorkspaceCalendar[],
  timeMin?: string,
  timeMax?: string,
  maxResults = 40,
): Promise<{ items: CalEvent[]; errors: { subject: string; error: string }[] }> {
  const settled = await Promise.allSettled(
    cals.map((c) => listCalendarEvents(c.subject, timeMin, timeMax, maxResults)),
  );

  const items: CalEvent[] = [];
  const errors: { subject: string; error: string }[] = [];
  settled.forEach((res, i) => {
    const cal = cals[i];
    if (res.status === 'fulfilled') {
      for (const ev of res.value.items || []) {
        items.push({ ...ev, accountSubject: cal.subject, accountLabel: cal.label });
      }
    } else {
      errors.push({ subject: cal.subject, error: String(res.reason) });
    }
  });

  // De-dupe a shared meeting that appears on multiple of Adam's calendars: key on
  // start+end+summary, keep the first (its account tag wins).
  const seen = new Set<string>();
  const deduped: CalEvent[] = [];
  for (const ev of items) {
    const key = [
      ev.start?.dateTime || ev.start?.date || '',
      ev.end?.dateTime || ev.end?.date || '',
      ev.summary || '',
    ].join('|');
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(ev);
  }
  deduped.sort((a, b) => {
    const as = a.start?.dateTime || a.start?.date || '';
    const bs = b.start?.dateTime || b.start?.date || '';
    return as < bs ? -1 : as > bs ? 1 : 0;
  });

  return { items: deduped, errors };
}

// @block 2.4 Gmail — list message stubs by labels. Use /messages/{id} for full body.
export type GmailMessageStub = { id: string; threadId?: string };
export type GmailMessageFull = {
  id: string;
  threadId?: string;
  snippet?: string;
  internalDate?: string;
  payload?: {
    headers?: { name: string; value: string }[];
  };
  labelIds?: string[];
};

export async function listGmailMessages(
  subject: string,
  labelIds: string[] = ['INBOX', 'UNREAD'],
  maxResults = 10,
): Promise<{ messages: GmailMessageStub[] }> {
  const params = new URLSearchParams({ subject, maxResults: String(maxResults) });
  for (const l of labelIds) params.append('labelIds', l);
  return req<{ messages: GmailMessageStub[] }>(
    `/api/hq/workspace/gmail/messages?${params}`,
  );
}

export async function getGmailMessage(subject: string, messageId: string): Promise<GmailMessageFull> {
  const params = new URLSearchParams({ subject, format: 'metadata' });
  return req<GmailMessageFull>(
    `/api/hq/workspace/gmail/messages/${encodeURIComponent(messageId)}?${params}`,
  );
}

// @section 3 Thread reader + AI reply (kills the bounce-to-Gmail dead link)

// @block 3.1 Full message with decoded plain-text body
export type GmailFull = {
  id: string;
  threadId?: string;
  subject: string;
  from: string;
  to?: string;
  date?: string;
  bodyText: string;
  snippet?: string;
};

function b64urlDecode(d: string): string {
  try {
    const s = d.replace(/-/g, '+').replace(/_/g, '/');
    return decodeURIComponent(
      atob(s + '==='.slice((s.length + 3) % 4))
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
  } catch {
    return '';
  }
}
function findTextPart(payload: any): string {
  if (!payload) return '';
  if ((payload.mimeType || '').startsWith('text/plain') && payload.body?.data) return b64urlDecode(payload.body.data);
  for (const p of payload.parts || []) {
    const r = findTextPart(p);
    if (r) return r;
  }
  // last resort: strip html
  if ((payload.mimeType || '').startsWith('text/html') && payload.body?.data) {
    return b64urlDecode(payload.body.data).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  return '';
}

export async function getGmailFull(subject: string, messageId: string): Promise<GmailFull> {
  const params = new URLSearchParams({ subject, format: 'full' });
  const m: any = await req(
    `/api/hq/workspace/gmail/messages/${encodeURIComponent(messageId)}?${params}`,
  );
  const h: { name: string; value: string }[] = m.payload?.headers ?? [];
  const get = (n: string) => h.find((x) => x.name.toLowerCase() === n.toLowerCase())?.value ?? '';
  return {
    id: m.id,
    threadId: m.threadId,
    subject: get('Subject'),
    from: get('From'),
    to: get('To'),
    date: get('Date'),
    snippet: m.snippet,
    bodyText: findTextPart(m.payload) || m.snippet || '',
  };
}

// @block 3.2 Send a reply via Bridge Workspace (WIF+DWD; subject = from-account)
export async function sendReply(
  subject: string,
  body: { to: string; subject: string; htmlBody: string; textBody?: string },
): Promise<void> {
  const params = new URLSearchParams({ subject });
  await req(`/api/hq/workspace/gmail/send?${params}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// @block 3.3 AI-drafted reply — OptiqOS /api/hq/email/draft (spawned backend).
// Until that endpoint deploys this returns {available:false} so the UI degrades gracefully.
export type DraftResult = { available: boolean; draft?: string; reason?: string };
export async function draftReply(input: {
  messageId: string;
  subject: string;
  from: string;
  bodyText: string;
}): Promise<DraftResult> {
  try {
    const r = await authFetch(`${API_BASE}/api/hq/email/draft`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    if (r.status === 404) return { available: false, reason: 'AI draft endpoint not deployed yet.' };
    if (!r.ok) return { available: false, reason: `Draft API ${r.status}: ${await r.text()}` };
    const j = await r.json();
    return { available: true, draft: j.draft ?? j.reply ?? '' };
  } catch (e) {
    return { available: false, reason: String(e) };
  }
}

// @section 4 Time-Value allocation
// Backend endpoint GET /api/hq/time-value/data is GATED (needs InternalOpsDb
// wiring + KV secret + OptiqOS API prod deploy). Until it returns 200, this
// transparently falls back to the real-data fixture snapshot so the route can
// be built + reviewed. Same graceful-degradation pattern as draftReply (3.3).
// When the endpoint deploys, no UI change is needed — it just starts using it.

export type TvAllocation = {
  bucket: string; cashFlowType: string; displayOrder: number;
  targetPct: number | null; capPct: number | null;
  actualPct: number; directMinutes: number; passiveMinutes: number;
  costUsd: number; sessionCount: number; deltaPp: number;
};
export type TvSession = {
  sessionId: string; bucket: string; bucketSecondary: string | null;
  confidence: number; decisionStage: string; decisionReason: string;
  directMinutes: number; passiveMinutes: number; promptTypingMinutes: number;
  aiFinishCoverage: number | null; walkAwayIntervals: number;
  costUsd: number; turnCount: number; project: string;
  bundle: string; tier: string; functionalCategory: string;
  firstSeenAt: string; prompt: string; tools: string;
};
export type TvTrend = { day: string; bucket: string; directMinutes: number };
export type TvReview = { total: number; veryLow: number; low: number; adminFallback: number };
export type TvInsight = {
  id: string; severity: 'ok' | 'watch' | 'stall';
  eyebrow: string; headline: string; detail: string; promptTemplate: string;
};
export type TvPayload = {
  generatedAt: string; user: string; windowDays: number;
  allocation: TvAllocation[]; sessions: TvSession[]; trend: TvTrend[];
  review: TvReview; insights: TvInsight[];
  source: 'endpoint' | 'fixture';
};

export async function getTimeValueData(
  from?: string, to?: string,
): Promise<TvPayload> {
  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  try {
    const r = await authFetch(`${API_BASE}/api/hq/time-value/data${qs.toString() ? `?${qs}` : ''}`);
    if (r.ok) return { ...(await r.json()), source: 'endpoint' };
    // 404 = endpoint not deployed yet → fall through to fixture
  } catch {
    // network/CORS → fall through to fixture
  }
  const fixture = (await import('./routes/_timeValueFixture.json')).default as unknown as Omit<TvPayload, 'source'>;
  return { ...fixture, source: 'fixture' };
}

// @section 5 Morning Metrics — deterministic daily Instantly + GA4 datasets
// Backend is LIVE in prod under /api/hq/morning-metrics (all GET, auth via
// authFetch Bearer). When the daily 5 AM AZ pipeline hasn't been configured
// the API returns 503 {"error":"morning_metrics_unconfigured"} — the helpers
// surface that as MorningMetricsUnconfigured so the route degrades gracefully
// (same spirit as the Time-Value fixture fallback at §4).

// @block 5.1 Shared metric shape (all counts are numbers; dates are ISO strings)
export type MmMetrics = {
  leads_total: number;
  sent: number;
  delivered: number;
  opened: number;
  replied: number;
  bounced: number;
  demo_engaged: number;
  diag_engaged: number;
};

export type MmTotals = MmMetrics & {
  snapshot_date: string | null;
  campaigns: number;
};

export type MmFreshness = {
  table_name: string;
  refreshed_at: string;
  rows_written: number | null;
  source_data_max_ts: string | null;
  refresh_run_id?: string | null;
};

export type MmSummary = {
  totals: MmTotals | null;
  freshness: MmFreshness[];
};

export type MmCampaign = MmMetrics & {
  campaign_id: string;
  campaign_name: string | null;
  vertical_id: string | null;
  snapshot_date: string;
};

export type MmTrendPoint = MmMetrics & {
  campaign_id: string;
  campaign_name: string | null;
  snapshot_date: string;
};

export type MmClicker = {
  lead_id: string | number;
  campaign_id: string | null;
  lead_email: string | null;
  company_name: string | null;
  state: string | null;
  utm_campaign: string | null;
  arm: string | null;
  demo_event_count: number;
  demo_engaged_seconds: number | null;
  diag_event_count: number;
  diag_engaged_seconds: number | null;
  first_seen_date: string | null;
  last_seen_date: string | null;
  tier: string | null;
};

// @block 5.2 503 sentinel — thrown when the pipeline isn't configured yet.
// Routes catch this to render a friendly inline notice instead of crashing.
export class MorningMetricsUnconfigured extends Error {
  constructor() {
    super('morning_metrics_unconfigured');
    this.name = 'MorningMetricsUnconfigured';
  }
}

async function mmReq<T>(path: string): Promise<T> {
  const res = await authFetch(`${API_BASE}${path}`);
  if (res.status === 503) {
    let body = '';
    try { body = await res.text(); } catch { /* ignore */ }
    if (body.includes('morning_metrics_unconfigured')) throw new MorningMetricsUnconfigured();
    throw new Error(`HQ API 503: ${body || res.statusText}`);
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HQ API ${res.status}: ${text || res.statusText}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// @block 5.3 Endpoints
export const getMorningMetricsSummary = () =>
  mmReq<MmSummary>('/api/hq/morning-metrics/summary');

export const getMorningMetricsCampaigns = () =>
  mmReq<MmCampaign[]>('/api/hq/morning-metrics/campaigns');

export const getMorningMetricsTrend = (campaignId?: string, days?: number) => {
  const qs = new URLSearchParams();
  if (campaignId) qs.set('campaignId', campaignId);
  if (days != null) qs.set('days', String(days));
  return mmReq<MmTrendPoint[]>(
    `/api/hq/morning-metrics/trend${qs.toString() ? `?${qs}` : ''}`,
  );
};

export const getMorningMetricsClickers = (tier?: string, limit?: number) => {
  const qs = new URLSearchParams();
  if (tier) qs.set('tier', tier);
  if (limit != null) qs.set('limit', String(limit));
  return mmReq<MmClicker[]>(
    `/api/hq/morning-metrics/clickers${qs.toString() ? `?${qs}` : ''}`,
  );
};

export const getMorningMetricsFreshness = () =>
  mmReq<MmFreshness[]>('/api/hq/morning-metrics/freshness');

// @section 6 Campaigns — analytics.v_campaign_metrics_* (campaign-engagement pipeline)
// Backend is LIVE in prod under /api/hq/campaigns (all GET, auth via Bearer).
// Same 5 AM AZ daily cadence as Morning Metrics — these views are richer
// (31 cols, reply classification breakdown, sender_health). 503 path mirrors
// the morning-metrics one but uses CampaignsUnconfigured.

// @block 6.1 Per-campaign row in Overview (latest snapshot, sent DESC ordered server-side)
export type CampaignOverviewRow = {
  campaign_id: string;
  campaign_name: string | null;
  vertical_id: string | null;
  taken_at: string;
  sent: number;
  bounced: number;
  unique_opens: number;
  unique_clicks: number;
  unique_replies: number;
  replies_engaged: number;
  replies_not_interested: number;
  replies_redirect: number;
  replies_auto_ooo: number;
  replies_auto_other: number;
  report_confirmed_browser: number;
  report_reads_over_10s: number;
  diag_tokens_visited: number;
  diag_tokens_real_browser: number;
  diag_questionnaire_filled: number;
  bookings_count: number;
};

export type CampaignOverviewTotals = {
  campaigns: number;
  sent: number;
  bounced: number;
  unique_opens: number;
  unique_clicks: number;
  unique_replies: number;
  replies_engaged: number;
  bookings: number;
  diag_filled: number;
  taken_at_max: string | null;
};

export type CampaignOverview = {
  totals: CampaignOverviewTotals | null;
  campaigns: CampaignOverviewRow[];
};

export type CampaignTimeseriesPoint = {
  taken_at: string;
  sent: number;
  bounced: number;
  unique_opens: number;
  unique_clicks: number;
  unique_replies: number;
  replies_engaged: number;
  replies_not_interested: number;
  replies_redirect: number;
  replies_auto_ooo: number;
  replies_auto_other: number;
  report_confirmed_browser: number;
  report_reads_over_10s: number;
  diag_tokens_visited: number;
  diag_tokens_real_browser: number;
  diag_questionnaire_filled: number;
  bookings_count: number;
};

export type CampaignDetailMeta = {
  campaign_id: string;
  campaign_name: string | null;
  vertical_id: string | null;
  last_taken_at: string;
};

export type CampaignDetailLatest = {
  taken_at: string;
  total_leads: number;
  sent: number;
  still_active: number;
  bounced: number;
  unsubscribed: number;
  unique_opens: number;
  unique_clicks: number;
  total_opens: number;
  unique_replies: number;
  replies_engaged: number;
  replies_not_interested: number;
  replies_redirect: number;
  replies_auto_ooo: number;
  replies_auto_other: number;
  replies_unclassified: number;
  report_distinct_slugs: number;
  report_confirmed_browser: number;
  report_reads_over_10s: number;
  report_reads_over_30s: number;
  diag_tokens_invited: number;
  diag_tokens_visited: number;
  diag_tokens_real_browser: number;
  diag_questionnaire_filled: number;
  diag_first_clicked_count: number;
  bookings_count: number;
  unique_senders_used: number;
  senders_with_recent_issues: number;
};

export type CampaignDetail = {
  meta: CampaignDetailMeta;
  latest: CampaignDetailLatest | null;
  timeseries: CampaignTimeseriesPoint[];
};

export type CampaignFreshness = {
  last_snapshot_at: string | null;
  tables: MmFreshness[];
};

// @block 6.2 503 sentinel
export class CampaignsUnconfigured extends Error {
  constructor() {
    super('campaigns_unconfigured');
    this.name = 'CampaignsUnconfigured';
  }
}

async function campReq<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${API_BASE}${path}`, init);
  if (res.status === 503) {
    let body = '';
    try { body = await res.text(); } catch { /* ignore */ }
    if (body.includes('campaigns_unconfigured')) throw new CampaignsUnconfigured();
    throw new Error(`HQ API 503: ${body || res.statusText}`);
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HQ API ${res.status}: ${text || res.statusText}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// @block 6.3 Endpoints
export const getCampaignsOverview = () =>
  campReq<CampaignOverview>('/api/hq/campaigns/overview');

export const getCampaignDetail = (campaignId: string, days?: number) => {
  const qs = days != null ? `?days=${days}` : '';
  return campReq<CampaignDetail>(`/api/hq/campaigns/${campaignId}/detail${qs}`);
};

export const getCampaignsFreshness = () =>
  campReq<CampaignFreshness>('/api/hq/campaigns/freshness');

// @section 7 Campaign masters — Adam's "campaign" grain (design.campaign_masters)
// Each master rolls up many wave/touch sends (design.campaigns). Atlas Overview
// reads at this grain by default; click a row → MasterDetail; click a child wave
// inside MasterDetail → existing /campaigns/:campaignId. Phase 2A.

// @block 7.1 Per-master row in Masters Overview
export type CampaignMasterRow = {
  master_key: string;                // "1", "2", … or "unassigned"
  campaign_master_id: number | null; // bigint nullable for unassigned bucket
  master_name: string;
  vertical_id: string;
  master_status: string;
  child_campaign_count: number;
  taken_at: string | null;
  sent: number;
  bounced: number;
  unique_opens: number;
  unique_clicks: number;
  unique_replies: number;
  replies_engaged: number;
  replies_not_interested: number;
  replies_redirect: number;
  replies_auto_ooo: number;
  replies_auto_other: number;
  report_confirmed_browser: number;
  report_reads_over_10s: number;
  diag_tokens_visited: number;
  diag_tokens_real_browser: number;
  diag_questionnaire_filled: number;
  bookings_count: number;
};

export type CampaignMastersTotals = {
  masters: number;
  child_campaigns: number;
  sent: number;
  bounced: number;
  unique_opens: number;
  unique_clicks: number;
  unique_replies: number;
  replies_engaged: number;
  bookings: number;
  diag_filled: number;
  taken_at_max: string | null;
};

export type CampaignMastersOverview = {
  totals: CampaignMastersTotals | null;
  masters: CampaignMasterRow[];
};

// Detail-view master is the row + the full 31-col latest aggregation
export type CampaignMasterDetailMaster = CampaignMasterRow & {
  total_leads: number;
  still_active: number;
  unsubscribed: number;
  total_opens: number;
  replies_unclassified: number;
  report_distinct_slugs: number;
  report_reads_over_30s: number;
  diag_tokens_invited: number;
  diag_first_clicked_count: number;
};

export type CampaignMasterTimeseriesPoint = {
  taken_at: string;
  sent: number;
  bounced: number;
  unique_opens: number;
  unique_clicks: number;
  unique_replies: number;
  replies_engaged: number;
  report_confirmed_browser: number;
  report_reads_over_10s: number;
  diag_tokens_visited: number;
  diag_tokens_real_browser: number;
  diag_questionnaire_filled: number;
  bookings_count: number;
};

export type CampaignMasterChild = {
  campaign_id: string;
  campaign_name: string | null;
  status: string | null;
  vertical_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  sent: number;
  unique_replies: number;
  replies_engaged: number;
  bookings_count: number;
  last_snapshot_at: string | null;
};

export type CampaignMasterDetail = {
  master: CampaignMasterDetailMaster;
  timeseries: CampaignMasterTimeseriesPoint[];
  children: CampaignMasterChild[];
};

// @block 7.2 Endpoints
export const getCampaignMastersOverview = () =>
  campReq<CampaignMastersOverview>('/api/hq/campaigns/masters/overview');

export const getCampaignMasterDetail = (masterKey: string, days?: number) => {
  const qs = days != null ? `?days=${days}` : '';
  return campReq<CampaignMasterDetail>(
    `/api/hq/campaigns/masters/${encodeURIComponent(masterKey)}/detail${qs}`,
  );
};

// @block 7.3 Touch rollup — (master x AZ-date x T1-T5+) cross-tab.
// Canonical read shape from /refresh-campaign-metrics Step 2.
// Send + GA4 attribution side-by-side per day-touch row.
export type CampaignTouchRow = {
  master: string;
  az_date: string;
  touch: string;
  sent: number;
  opens: number;
  open_pct: number | null;
  replies: number;
  engaged: number;
  redirect: number;
  ga4_sessions: number;
  ga4_leads: number;
};

export type CampaignTouchRollup = {
  window_days: number;
  rows: CampaignTouchRow[];
};

export const getCampaignTouchRollup = (windowDays?: number) => {
  const qs = windowDays != null ? `?window_days=${windowDays}` : '';
  return campReq<CampaignTouchRollup>(`/api/hq/campaigns/touch-rollup${qs}`);
};

// @block 7.4 Daily snapshot — Atlas /campaigns/daily "Yesterday's Report".
// Four cards (cold_email + ga4_engagement live; social + websites placeholders
// until automated post-tracking and BigQuery export are wired). Server resolves
// the effective AZ date when ?date= is omitted (latest day with at least one
// row in analytics.outreach_send_facts).
export type CampaignDailyColdEmail = {
  sent: number;
  opens: number;
  open_pct: number;
  total_replies: number;
  human_replies: number;
  other_replies: number;
  reply_pct: number;
  human_reply_pct: number;
  other_reply_pct: number;
  breakdown: {
    auto_ooo: number;
    auto_other: number;
    unclassified: number;
    unattributed: number;
  };
  // Drill lists; bounded server-side (<=50 rows).
  human_reply_list: CampaignDailyReplyRow[];
  other_reply_list: CampaignDailyReplyRow[];
};

export type CampaignDailyReplyRow = {
  from_address: string | null;
  subject: string | null;
  classification: string | null;
  timestamp: string | null;
};

export type CampaignDailyLeadRow = {
  lead_id: string;
  firm_key: string | null;
  lead_state: string | null;
  dwell_sec: number;
};

export type CampaignDailyFirmRow = {
  firm_domain: string;
  human_visits: number;
  other_visits: number;
};

export type CampaignDailyGa4Surface = {
  human_visits: number;
  other_visits: number;
  human_pct: number;
  other_pct: number;
  distinct_human_leads: number;
  human_lead_pct: number;
  lead_list: CampaignDailyLeadRow[];
};

export type CampaignDailyPlaceholder = {
  source?: 'not_wired';
  status: 'not_wired';
  message: string;
};

// Teardown surface = daily rollup for audit.optiq-os.com host on property
// 531592976. Two backing surfaces:
//   source='classified' = analytics.outreach_audit_sessions on internal_outreach,
//                         rebuilt nightly at 12 UTC by build_outreach_send_facts.py.
//                         Uses the canonical classifier (IP-geo datacenter blocklist
//                         + business-state match against the lead's known state,
//                         with a tbd bucket). Same classifier the Demo card uses.
//   source='bq_live'    = inline classifier over raw.ga4_bq_events on
//                         internal_warehouse. Used only when the nightly view has
//                         not yet covered the requested day (e.g. today partial).
//                         Weaker classifier (no state-match), no tbd bucket.
// firm slug is extracted from the ?firm= query param in page_location.
export type CampaignDailyTeardownSurface = {
  source: 'classified' | 'bq_live';
  human_visits: number;
  other_visits: number;
  human_pct: number;
  other_pct: number;
  distinct_firms_total: number;     // distinct firms hit (any visitor)
  distinct_firms_human: number;     // distinct firms with at least one human
  human_firm_pct: number;           // distinct_firms_human / distinct_firms_total
  firm_list: CampaignDailyFirmRow[];
};

export type CampaignDailyGa4Engagement = {
  demo: CampaignDailyGa4Surface | null;
  teardown: CampaignDailyTeardownSurface | CampaignDailyPlaceholder;
};

export function isTeardownPlaceholder(
  t: CampaignDailyTeardownSurface | CampaignDailyPlaceholder | null | undefined
): t is CampaignDailyPlaceholder {
  return !!t && (t as CampaignDailyPlaceholder).status === 'not_wired';
}

export type CampaignDailySnapshot = {
  effective_date: string;   // YYYY-MM-DD (AZ)
  today_az: string;         // YYYY-MM-DD (AZ)
  cold_email: CampaignDailyColdEmail | null;
  ga4_engagement: CampaignDailyGa4Engagement | null;
  social: CampaignDailyPlaceholder;
  websites: CampaignDailyPlaceholder;
};

export const getCampaignDailySnapshot = (date?: string) => {
  const qs = date ? `?date=${encodeURIComponent(date)}` : '';
  return campReq<CampaignDailySnapshot>(`/api/hq/campaigns/daily${qs}`);
};

// @block 7.5 GA4 owned-properties live snapshot — Atlas /campaigns/ga4.
// Same JSON shape the committed fixture had (sites + daily + freshness +
// totals), now sourced live from internal_warehouse.raw.ga4_bq_events on
// the backend. Lib type `Ga4Fixture` (lib/ga4.ts) is the consumer.
export type Ga4LiveTotals = {
  events_total: number;
  visitors_total: number;
  visitors_human: number;
  visitors_bot: number;
  human_pct: number;
};
export type Ga4LiveSite = {
  host: string;
  events_total: number;
  sessions_total: number;
  visitors_total: number;
  visitors_human: number;
  visitors_bot: number;
  human_pct: number;
  last_event: string;
};
export type Ga4LiveDayPoint = { date: string; human: number; bot: number; total: number };
export type Ga4LiveDailySeries = { host: string; series: Ga4LiveDayPoint[] };
export type Ga4LiveFreshness = {
  property_id: string;
  stream_id: string;
  last_event: string;
  days_behind: number;
};
export type Ga4LiveSnapshot = {
  as_of: string;
  window_days: number;
  totals: Ga4LiveTotals;
  sites: Ga4LiveSite[];
  daily: Ga4LiveDailySeries[];
  freshness: Ga4LiveFreshness[];
  note?: string;
};

export const getCampaignGa4Live = () =>
  campReq<Ga4LiveSnapshot>('/api/hq/campaigns/ga4');

// @block 7.6 Leads — engaged-prospect cohort (the old Morning Metrics clickers,
// merged in). A lead = a prospect who clicked (demo/diag).
export type CampaignLead = {
  lead_id: string;
  campaign_id: string | null;
  lead_email: string | null;
  company_name: string | null;
  state: string | null;
  utm_campaign: string | null;
  arm: string | null;
  demo_event_count: number;
  demo_engaged_seconds: number | null;
  diag_event_count: number;
  diag_engaged_seconds: number | null;
  first_seen_date: string | null;
  last_seen_date: string | null;
  tier: string | null;
};

export const getCampaignLeads = (opts?: {
  masterKey?: string; cohort?: string; tier?: string; search?: string; limit?: number;
}) => {
  const qs = new URLSearchParams();
  if (opts?.masterKey) qs.set('masterKey', opts.masterKey);
  if (opts?.cohort) qs.set('cohort', opts.cohort);
  if (opts?.tier) qs.set('tier', opts.tier);
  if (opts?.search) qs.set('search', opts.search);
  if (opts?.limit != null) qs.set('limit', String(opts.limit));
  return campReq<CampaignLead[]>(`/api/hq/campaigns/leads${qs.toString() ? `?${qs}` : ''}`);
};

// @block 7.7 Daily activity — TRUE per-day counts keyed by event date
// (analytics.v_campaign_(master_)daily_activity). Replaces client-side
// snapshot-differencing; demo_clicks / diag_visits are now reliable.
export type CampaignDailyActivity = {
  activity_date: string;       // YYYY-MM-DD
  child_campaign_count?: number; // master rollup only
  sent: number;
  opens: number;
  bounced: number;
  replies: number;
  replies_engaged: number;
  demo_clicks: number;
  diag_visits: number;
  booked: number;
  filled?: number;             // questionnaire completions; optional until the API deploy lands
};

export const getCampaignMasterDaily = (masterKey: string, days?: number) => {
  const qs = days != null ? `?days=${days}` : '';
  return campReq<CampaignDailyActivity[]>(
    `/api/hq/campaigns/masters/${encodeURIComponent(masterKey)}/daily${qs}`,
  );
};

export const getCampaignDaily = (campaignId: string, days?: number) => {
  const qs = days != null ? `?days=${days}` : '';
  return campReq<CampaignDailyActivity[]>(`/api/hq/campaigns/${campaignId}/daily${qs}`);
};

// @block 7.8 Replies — the engaged-reply triage surface. A reply = a received
// human reply (engaged/redirect/not-interested) joined to its ops.reply_triage
// state. Read the queue, then act: mark sent/skipped, or send a 1:1 reply via
// the Bridge Workspace SA (server sends from adam_caccamise@signal-rev.com).
export type CampaignReply = {
  email_id: string;
  thread_id: string | null;
  from_address: string | null;
  subject: string | null;
  body_text: string | null;
  classification: string | null;             // human_engaged | human_redirect | human_notinterested
  classification_confidence: number | null;
  timestamp_email: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  master_id: number | null;
  master_name: string | null;
  response_status: string | null;            // null = untriaged; else pending | sent | skipped
  triage_action: string | null;
  triage_classification: string | null;
  suggested_subject: string | null;
  suggested_body: string | null;
  retarget_to: string | null;
  notes: string | null;
  triaged_at: string | null;
  triaged_by: string | null;
};

export const getCampaignReplies = (opts?: {
  masterKey?: string; classification?: string; status?: string; search?: string; limit?: number;
}) => {
  const qs = new URLSearchParams();
  if (opts?.masterKey) qs.set('masterKey', opts.masterKey);
  if (opts?.classification) qs.set('classification', opts.classification);
  if (opts?.status) qs.set('status', opts.status);
  if (opts?.search) qs.set('search', opts.search);
  if (opts?.limit != null) qs.set('limit', String(opts.limit));
  return campReq<CampaignReply[]>(`/api/hq/campaigns/replies${qs.toString() ? `?${qs}` : ''}`);
};

// Mark a reply sent/skipped (writes ops.reply_triage). No email is sent.
export const triageReply = (emailId: string, body: { status: 'sent' | 'skipped' | 'pending'; notes?: string }) =>
  campReq<{ email_id: string; response_status: string }>(
    `/api/hq/campaigns/replies/${encodeURIComponent(emailId)}/triage`,
    { method: 'POST', body: JSON.stringify(body) },
  );

// Send a 1:1 reply. Server sends via the Bridge Workspace SA from
// adam_caccamise@signal-rev.com, then marks the triage row sent.
export const sendCampaignReply = (
  emailId: string,
  body: { to: string; subject: string; htmlBody: string; textBody?: string },
) =>
  campReq<unknown>(
    `/api/hq/campaigns/replies/${encodeURIComponent(emailId)}/send`,
    { method: 'POST', body: JSON.stringify(body) },
  );

// @section 8 My-Day blocks — "Frame my day" (daily-materialized Available blocks)
//
// Writes real Google Calendar events on Adam's PRIMARY calendar marked
// transparency:"transparent" (Free/Available — clients can still book over
// them) so the My Day Today panel shows a structured maker schedule. Blocks are
// materialized per-day on demand, trimmed around real (opaque) meetings —
// sub-segments under 45 min are dropped — and idempotent: each slot carries an
// extendedProperties.private.atlasSlotKey, so re-running skips slots that
// already exist. No patch/delete is used (the DWD calendar surface exposes
// neither create-only): a stale transparent block left when a meeting moves is
// harmless (it's Free) and Adam can clear it natively in Google Calendar.

// @block 8.1 Maker-schedule template (Adam-chosen 2026-05-19). Mon–Fri
// 07:00–11:00 Deep Work, 13:00–17:00 Build/Ops; every day 17:30–20:00 Family
// Time. Midday 11:00–13:00 is intentionally left open for client meetings.
export type DayBlockDef = {
  key: string;
  label: string;
  days: number[];          // weekday, 0=Sun … 6=Sat
  start: [number, number]; // [hour, minute] AZ wall clock
  end: [number, number];
};
export const MAKER_BLOCKS: DayBlockDef[] = [
  { key: 'deep-work', label: 'Deep Work', days: [1, 2, 3, 4, 5], start: [7, 0], end: [11, 0] },
  { key: 'build-ops', label: 'Build / Ops', days: [1, 2, 3, 4, 5], start: [13, 0], end: [17, 0] },
  { key: 'family-time', label: 'Family Time', days: [0, 1, 2, 3, 4, 5, 6], start: [17, 30], end: [20, 0] },
];
const MIN_SLOT_MIN = 45;
const AZ_TZ = 'America/Phoenix'; // no DST — fixed UTC-7 year-round
const AZ_OFFSET = '-07:00';

export type SeedDaySummary = {
  date: string;
  created: number;
  skippedExisting: number;
  trimmed: number;    // created slots that were shortened around a meeting
  blockedOut: number; // template blocks fully covered by meetings
};

// @block 8.2 Timezone-correct helpers (independent of the browser locale)
export function azToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: AZ_TZ }).format(new Date());
}
function weekdayOf(dateStr: string): number {
  // Weekday of a calendar date is tz-independent given Y-M-D.
  return new Date(`${dateStr}T00:00:00Z`).getUTCDay();
}
function azDateTime(dateStr: string, h: number, m: number): string {
  return `${dateStr}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00${AZ_OFFSET}`;
}
// Minutes-from-midnight (AZ) for an instant, clamped to the target AZ day.
function azMinsOnDay(iso: string, dateStr: string): number | null {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: AZ_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(d).map((p) => [p.type, p.value]),
  );
  const evDate = `${parts.year}-${parts.month}-${parts.day}`;
  if (evDate < dateStr) return 0;
  if (evDate > dateStr) return 24 * 60;
  let hh = parseInt(parts.hour, 10);
  if (hh === 24) hh = 0;
  return hh * 60 + parseInt(parts.minute, 10);
}

// @block 8.3 Create a single event (proxied → Bridge WorkspaceCalendarCreateEventRequest:
// { eventBodyJson (opaque Google event resource, stringified), calendarId }).
export async function createCalendarEvent(
  subject: string,
  event: Record<string, unknown>,
  calendarId = 'primary',
): Promise<{ id: string }> {
  const params = new URLSearchParams({ subject });
  return req<{ id: string }>(`/api/hq/workspace/calendar/events?${params}`, {
    method: 'POST',
    body: JSON.stringify({ eventBodyJson: JSON.stringify(event), calendarId }),
  });
}

// @block 8.4 Materialize a day's blocks — trimmed around meetings, idempotent
export async function seedDayBlocks(
  subject: string,
  dateStr: string = azToday(),
): Promise<SeedDaySummary> {
  const dayStart = `${dateStr}T00:00:00${AZ_OFFSET}`;
  const dayEnd = `${dateStr}T23:59:59${AZ_OFFSET}`;
  const { items } = await listCalendarEvents(subject, dayStart, dayEnd, 250);

  const existingKeys = new Set<string>();
  const busy: Array<[number, number]> = [];
  for (const e of items) {
    const priv = e.extendedProperties?.private;
    if (priv?.atlasManaged === 'true') {
      if (priv.atlasSlotKey) existingKeys.add(priv.atlasSlotKey);
      continue; // our own blocks never count as busy
    }
    if (e.status === 'cancelled') continue;
    if (e.transparency === 'transparent') continue; // Free events don't block
    if (!e.start?.dateTime || !e.end?.dateTime) continue; // all-day / open-ended
    const s = azMinsOnDay(e.start.dateTime, dateStr);
    const en = azMinsOnDay(e.end.dateTime, dateStr);
    if (s == null || en == null || en <= s) continue;
    busy.push([s, en]);
  }
  busy.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const iv of busy) {
    const last = merged[merged.length - 1];
    if (last && iv[0] <= last[1]) last[1] = Math.max(last[1], iv[1]);
    else merged.push([iv[0], iv[1]]);
  }

  const wd = weekdayOf(dateStr);
  const sum: SeedDaySummary = { date: dateStr, created: 0, skippedExisting: 0, trimmed: 0, blockedOut: 0 };

  for (const b of MAKER_BLOCKS) {
    if (!b.days.includes(wd)) continue;
    const bStart = b.start[0] * 60 + b.start[1];
    const bEnd = b.end[0] * 60 + b.end[1];
    let free: Array<[number, number]> = [[bStart, bEnd]];
    for (const [ms, me] of merged) {
      const next: Array<[number, number]> = [];
      for (const [fs, fe] of free) {
        if (me <= fs || ms >= fe) { next.push([fs, fe]); continue; }
        if (ms > fs) next.push([fs, Math.min(ms, fe)]);
        if (me < fe) next.push([Math.max(me, fs), fe]);
      }
      free = next;
    }
    const usable = free.filter(([s, e]) => e - s >= MIN_SLOT_MIN);
    if (usable.length === 0) { sum.blockedOut++; continue; }
    for (const [s, e] of usable) {
      const slotKey = `${b.key}|${dateStr}|${s}`;
      if (existingKeys.has(slotKey)) { sum.skippedExisting++; continue; }
      const ev = {
        summary: b.label,
        description: 'Auto-framed by Atlas · Available — clients may book over this block.',
        start: { dateTime: azDateTime(dateStr, Math.floor(s / 60), s % 60), timeZone: AZ_TZ },
        end: { dateTime: azDateTime(dateStr, Math.floor(e / 60), e % 60), timeZone: AZ_TZ },
        transparency: 'transparent',
        visibility: 'private',
        reminders: { useDefault: false },
        extendedProperties: {
          private: { atlasManaged: 'true', atlasBlock: b.key, atlasDate: dateStr, atlasSlotKey: slotKey },
        },
      };
      await createCalendarEvent(subject, ev);
      sum.created++;
      if (s !== bStart || e !== bEnd) sum.trimmed++;
    }
  }
  return sum;
}

// @block 8.5 Is this a Frame-my-day / Available block? (used by My Day to style
// framed blocks as a faint band so real meetings visually layer on top).
export function isFramedBlock(e: CalEvent): boolean {
  return e.extendedProperties?.private?.atlasManaged === 'true' || e.transparency === 'transparent';
}

// @section 9 Accounts — unified account-graph surface (CRM+ERP merger, Slice A)
//
// Atlas collapses CRM/ERP/Delivery/PM/BD lens UIs into one Account workspace.
// hq_crm.companies IS the account-graph root — every other schema FKs into it.
// Two endpoints back the new surface:
//   GET /api/hq/accounts                 → directory with rollups
//   GET /api/hq/accounts/{companyId}     → aggregator backing the workspace shell
// Lens detail still loads via existing per-lens endpoints (/crm, /delivery, …).

// @block 9.1 Directory row — sortable/filterable in AccountsDirectory.
export type AccountListItem = {
  id: string;
  name: string;
  domain?: string | null;
  industry?: string | null;
  optiqOrgId?: string | null;
  openDealCount: number;
  engagementCount: number;
  lastActivityAt?: string | null;
};

export type AccountListFilters = {
  q?: string;
  hasOpenDeal?: boolean;
  hasEngagement?: boolean;
  take?: number;
};

export const listAccounts = (f?: AccountListFilters) => {
  const qs = new URLSearchParams();
  if (f?.q) qs.set('q', f.q);
  if (f?.hasOpenDeal) qs.set('hasOpenDeal', 'true');
  if (f?.hasEngagement) qs.set('hasEngagement', 'true');
  if (f?.take != null) qs.set('take', String(f.take));
  return req<AccountListItem[]>(`/api/hq/accounts${qs.toString() ? `?${qs}` : ''}`);
};

// @block 9.2 Aggregator payload — backs the AccountWorkspace header + Overview lens.
export type AccountCompany = {
  id: string;
  name: string;
  domain?: string | null;
  industry?: string | null;
  employeeCount?: number | null;
  notes?: string | null;
  optiqOrgId?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AccountDeal = {
  id: string;
  name: string;
  stage: string;
  valueCents?: number | null;
  currency: string;
  expectedClose?: string | null;
  closedAt?: string | null;
  ownerId?: string | null;
};

export type AccountActivity = {
  id: string;
  activityType: string;
  subject?: string | null;
  occurredAt: string;
  dealId?: string | null;
  contactId?: string | null;
  externalSource?: string | null;
};

export type AccountEngagement = {
  id: string;
  name: string;
  status: string;
  startsOn?: string | null;
  endsOn?: string | null;
  ownerId?: string | null;
};

export type AccountBdRollup = {
  factsCount: number;
  asksOpen: number;
  findingsToResearch: number;
  latestOffer: {
    id: string;
    version: number;
    status: string;
    totalCents?: number | null;
    sentAt?: string | null;
  } | null;
};

// @block 9.3 AccountTask — open tasks rolled up to this account via the
// polymorphic parent (deal / engagement / workstream / deliverable / company).
// Returned inline on the aggregator so the Workplan lens (Slice F) needs no
// extra fetch.
export type AccountTask = {
  id: string;
  title: string;
  status: string;
  priority: number;
  dueDate?: string | null;
  parentType: string;
  parentId?: string | null;
  assigneeId?: string | null;
  createdAt: string;
};

export type AccountAggregate = {
  company: AccountCompany;
  deals: AccountDeal[];
  contactsCount: number;
  recentActivities: AccountActivity[];
  engagements: AccountEngagement[];
  bd: AccountBdRollup;
  openTasksCount: number;
  openTasks: AccountTask[];
  financialsSummary: null; // Slice D wires this once hq_erp views are sharpened.
};

export const getAccount = (companyId: string) =>
  req<AccountAggregate>(`/api/hq/accounts/${encodeURIComponent(companyId)}`);

// @section 10 BD — Patrick's lens inside AccountWorkspace (CRM+ERP merger, Slice E)
//
// Closes the "hq_bd has rich schema, ZERO web routes" gap. Four sub-domains
// scoped by company_id (with deal_id required on writes since hq_bd FKs to a
// specific deal). Offers are deal-scoped but listable account-wide via the
// API's deals join.

// @block 10.1 Facts — given company facts (key/value pairs)
export type BdFact = {
  id: string;
  dealId: string;
  companyId: string;
  key: string;
  value: string;
  source?: string | null;
  sourceUrl?: string | null;
  createdAt: string;
  updatedAt: string;
};

export const listBdFacts = (companyId: string) =>
  req<BdFact[]>(`/api/hq/bd/facts?companyId=${encodeURIComponent(companyId)}`);

export const createBdFact = (body: {
  dealId: string; companyId: string;
  key: string; value: string;
  source?: string; sourceUrl?: string;
}) => req<BdFact>('/api/hq/bd/facts', { method: 'POST', body: JSON.stringify(body) });

export const patchBdFact = (id: string, body: {
  value?: string; source?: string; sourceUrl?: string;
}) => req<BdFact>(`/api/hq/bd/facts/${encodeURIComponent(id)}`, {
  method: 'PATCH', body: JSON.stringify(body),
});

// @block 10.2 Asks — what the client said they want
export type BdAsk = {
  id: string;
  dealId: string;
  companyId: string;
  ask: string;
  detail?: string | null;
  status: string;          // open | addressed-in-offer | dropped | deferred
  priority?: string | null; // high | medium | low
  sourceActivityId?: string | null;
  createdAt: string;
  updatedAt: string;
};

export const listBdAsks = (companyId: string, status?: string) => {
  const qs = new URLSearchParams({ companyId });
  if (status) qs.set('status', status);
  return req<BdAsk[]>(`/api/hq/bd/asks?${qs}`);
};

export const createBdAsk = (body: {
  dealId: string; companyId: string;
  ask: string; detail?: string;
  priority?: string;
  sourceActivityId?: string;
}) => req<BdAsk>('/api/hq/bd/asks', { method: 'POST', body: JSON.stringify(body) });

export const patchBdAsk = (id: string, body: {
  status?: string; priority?: string; detail?: string;
}) => req<BdAsk>(`/api/hq/bd/asks/${encodeURIComponent(id)}`, {
  method: 'PATCH', body: JSON.stringify(body),
});

// @block 10.3 Findings — discovered research items
export type BdFinding = {
  id: string;
  dealId: string;
  companyId: string;
  category: string;
  title: string;
  body?: string | null;
  sourceSystem?: string | null;
  sourceUrl?: string | null;
  severity?: string | null;
  status: string;          // to-research | in-progress | done | dropped
  assignedTo?: string | null;
  dueAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export const listBdFindings = (companyId: string, status?: string) => {
  const qs = new URLSearchParams({ companyId });
  if (status) qs.set('status', status);
  return req<BdFinding[]>(`/api/hq/bd/findings?${qs}`);
};

export const createBdFinding = (body: {
  dealId: string; companyId: string;
  category: string; title: string; body?: string;
  sourceSystem?: string; sourceUrl?: string; severity?: string;
  status?: string; assignedTo?: string; dueAt?: string;
}) => req<BdFinding>('/api/hq/bd/findings', { method: 'POST', body: JSON.stringify(body) });

export const patchBdFinding = (id: string, body: {
  status?: string; assignedTo?: string;
  dueAt?: string; completedAt?: string;
  body?: string; severity?: string;
}) => req<BdFinding>(`/api/hq/bd/findings/${encodeURIComponent(id)}`, {
  method: 'PATCH', body: JSON.stringify(body),
});

// @block 10.4 Offers — versioned scopes / proposals (deal-scoped)
export type BdOffer = {
  id: string;
  dealId: string;
  version: number;
  status: string;          // draft | sent | accepted | rejected | superseded
  summary?: string | null;
  body?: string | null;
  totalCents?: number | null;
  currency: string;
  sentAt?: string | null;
  decidedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export const listBdOffers = (opts: { companyId?: string; dealId?: string }) => {
  const qs = new URLSearchParams();
  if (opts.companyId) qs.set('companyId', opts.companyId);
  if (opts.dealId) qs.set('dealId', opts.dealId);
  return req<BdOffer[]>(`/api/hq/bd/offers?${qs}`);
};

export const createBdOffer = (body: {
  dealId: string;
  summary?: string; body?: string;
  totalCents?: number; currency?: string;
}) => req<BdOffer>('/api/hq/bd/offers', { method: 'POST', body: JSON.stringify(body) });

// @block 10.5 Offer items — Ask→Offer pipeline (close the BD loop)
// addressesAskId is the pointer back to the Ask this line item solves; the
// BD lens uses it to badge offers with the asks they cover.
export type BdOfferItem = {
  id: string;
  offerId: string;
  name: string;
  description?: string | null;
  qty: number;
  unitCents: number;
  lineTotalCents: number;
  recurring?: string | null;
  addressesAskId?: string | null;
  sortOrder: number;
  createdAt: string;
};

export const listBdOfferItems = (offerId: string) =>
  req<BdOfferItem[]>(`/api/hq/bd/offers/${encodeURIComponent(offerId)}/items`);

export const createBdOfferItem = (offerId: string, body: {
  name: string;
  description?: string;
  qty?: number;
  unitCents?: number;
  recurring?: string;
  addressesAskId?: string;
}) => req<BdOfferItem>(
  `/api/hq/bd/offers/${encodeURIComponent(offerId)}/items`,
  { method: 'POST', body: JSON.stringify(body) },
);

// @block 10.6 Users — assignee picker for Workplan create form + finding assign
export type HqUser = {
  userId: string;
  displayName: string;
  email: string;
  timezone: string;
};

export const listUsers = () => req<HqUser[]>('/api/hq/users');

// @section 11 Relationship lens — account-scoped CRM (CRM+ERP merger, Slice B)
//
// New helpers that scope existing /api/hq/crm/* endpoints by companyId. The
// legacy global helpers (listContacts/listDeals) stay untouched so the old
// /crm route keeps working until its redirect lands in Slice F.

// @block 11.1 Account-scoped contacts (extends /api/hq/crm/contacts with companyId)
export const listAccountContacts = (companyId: string, q?: string) => {
  const qs = new URLSearchParams({ companyId });
  if (q) qs.set('q', q);
  return req<Contact[]>(`/api/hq/crm/contacts?${qs}`);
};

// @block 11.2 Account-scoped deals
export const listAccountDeals = (companyId: string, stage?: string) => {
  const qs = new URLSearchParams({ companyId });
  if (stage) qs.set('stage', stage);
  return req<Deal[]>(`/api/hq/crm/deals?${qs}`);
};

// @block 11.3 Deal create — was missing from the legacy helpers entirely
export const createDeal = (body: {
  name: string;
  companyId?: string;
  primaryContactId?: string;
  stage?: string;
  valueCents?: number;
  probability?: number;
  expectedClose?: string;
}) => req<Deal>('/api/hq/crm/deals', { method: 'POST', body: JSON.stringify(body) });

// @block 11.4 Activity timeline — account-scoped read + manual create
export type Activity = {
  id: string;
  activityType: string;       // call | email | meeting | sms | note | task | other
  subject?: string | null;
  dealId?: string | null;
  contactId?: string | null;
  occurredAt: string;
};

export const listAccountActivities = (companyId: string) =>
  req<Activity[]>(`/api/hq/crm/activities?companyId=${encodeURIComponent(companyId)}`);

export const createActivity = (body: {
  activityType: string;
  subject?: string;
  body?: string;
  dealId?: string;
  contactId?: string;
  companyId?: string;
}) => req<Activity>('/api/hq/crm/activities', { method: 'POST', body: JSON.stringify(body) });

// @section 12 Delivery lens — engagements → workstreams → deliverables (Slice C)
//
// Account-scoped reads (companyId on engagements; engagementId on workstreams +
// deliverables). The existing /delivery route still uses listEngagements()
// without companyId; this lens uses listAccountEngagements.

export type Engagement = {
  id: string;
  name: string;
  companyId?: string | null;
  status: string;            // active | paused | done | archived
  createdAt: string;
};

export type Workstream = {
  id: string;
  engagementId: string;
  name: string;
  status: string;
};

export type Deliverable = {
  id: string;
  workstreamId: string;
  title: string;
  status: string;            // draft | in_review | client_review | approved | rejected
  driveUrl?: string | null;
  fileVersion: number;
  dueDate?: string | null;
};

// @block 12.1 Account-scoped engagement list
export const listAccountEngagements = (companyId: string, status?: string) => {
  const qs = new URLSearchParams({ companyId });
  if (status) qs.set('status', status);
  return req<Engagement[]>(`/api/hq/delivery/engagements?${qs}`);
};

export const createEngagement = (body: {
  name: string;
  companyId?: string;
  dealId?: string;
  summary?: string;
}) => req<Engagement>('/api/hq/delivery/engagements', { method: 'POST', body: JSON.stringify(body) });

// @block 12.2 Workstreams (engagementId-scoped)
export const listWorkstreams = (engagementId: string) =>
  req<Workstream[]>(`/api/hq/delivery/workstreams?engagementId=${encodeURIComponent(engagementId)}`);

export const createWorkstream = (body: {
  engagementId: string;
  name: string;
  description?: string;
}) => req<Workstream>('/api/hq/delivery/workstreams', { method: 'POST', body: JSON.stringify(body) });

// @block 12.3 Deliverables (per workstream or per engagement)
export const listAccountDeliverables = (opts: { workstreamId?: string; engagementId?: string }) => {
  const qs = new URLSearchParams();
  if (opts.workstreamId) qs.set('workstreamId', opts.workstreamId);
  if (opts.engagementId) qs.set('engagementId', opts.engagementId);
  return req<Deliverable[]>(`/api/hq/delivery/deliverables?${qs}`);
};

export const createDeliverable = (body: {
  workstreamId: string;
  title: string;
  description?: string;
  driveFileId?: string;
  driveUrl?: string;
  dueDate?: string;
}) => req<Deliverable>('/api/hq/delivery/deliverables', { method: 'POST', body: JSON.stringify(body) });

// @section 13 Founder-OS — Atlas v5 read+write surface backed by hq_bd.queue_items,
// hq_crm.{companies,deals,contacts}, hq_strategy.initiatives. Schema in
// OptiqHQ migration 016, seed in 017. Endpoints in OptiqOS
// Features/Hq/HqFounderOsEndpoints.cs.

// @block 13.1 Top bar — pipeline value + 7d delta + support metrics
export type FoTopBar = {
  pipeline_total_cents: number;
  week_delta_cents: number;
  open_deal_count: number;
  mrr_cents: number;
  cash_cents: number;
  cash_months: number | null;
  bookings_7d: number;
  captures_pending: number;
};
export const foGetTopBar = () => req<FoTopBar>('/api/hq/founder-os/top-bar');

// @block 13.2 Pipeline — 5 stages with cards
export type FoCard = {
  deal_id: string;
  stage: 'lead' | 'qualified' | 'proposal' | 'negotiation' | 'won';
  company_id: string | null;
  name: string;
  badge: string;
  warmth: 'engaged' | 'warm' | 'stall' | 'cold';
  heat: string;
  meta: string | null;
  deal_cents: number | null;
  decay_band: 'fresh' | 'warm' | 'aging' | 'stale' | 'cold';
  age_label: string;
};
export type FoPipelineStage = {
  key: 'lead' | 'qualified' | 'proposal' | 'negotiation' | 'won';
  label: string;
  count: number;
  cards: FoCard[];
};
export const foGetPipeline = () => req<FoPipelineStage[]>('/api/hq/founder-os/pipeline');

// Opportunity detail — the per-deal screen behind a pipeline card (GET) + recommendation verdict (POST).
export type OppActivity = {
  id: string; occurred_at: string; activity_type: string | null; direction: string | null;
  subject: string | null; body: string | null; created_by: string | null;
  external_source: string | null; counterparty_name: string | null;
};
export type OpportunityDetail = {
  opportunity: {
    deal_id: string; name: string; stage: string | null; bd_stage: string | null;
    objective: string | null; value_cents: number | null; currency: string | null;
    heat_score: number | null; momentum_pct: number | null; decay_band: string | null;
    last_activity_at: string | null; closed_at: string | null;
  };
  company: { id: string; name: string | null; domain: string | null; warmth_band: string | null } | null;
  primary_contact: { id: string; name: string | null; email: string | null; title: string | null } | null;
  recommendation: { id: string; recommended_action: string | null; occurred_at: string; created_by: string | null } | null;
  timeline: OppActivity[];
};
export const getOpportunity = (dealId: string) =>
  req<OpportunityDetail>(`/api/hq/opportunities/${encodeURIComponent(dealId)}`);
export const postRecommendationVerdict = (activityId: string, status: 'done' | 'skipped') =>
  req<{ id: string; recommendation_status: string }>(
    `/api/hq/opportunities/recommendations/${encodeURIComponent(activityId)}/verdict`,
    { method: 'POST', body: JSON.stringify({ status }) });

// @block 13.3 Stall banner + cold backlog
export type FoStallItem = { company_id: string; badge: string; name: string; days_quiet: number | null };
export type FoColdItem  = { company_id: string; badge: string; name: string };
export const foGetStallBanner = () => req<FoStallItem[]>('/api/hq/founder-os/stall-banner');
export const foGetColdBacklog = () => req<FoColdItem[]>('/api/hq/founder-os/cold-backlog');

// @block 13.4 Account record (right-rail aggregate)
export type FoAccountRecord = {
  company: { id: string; name: string; domain?: string | null; industry?: string | null; badge: string; warmth?: string | null; last_touched_at?: string | null };
  deal: { id: string; stage: string; value_cents?: number | null; heat: string; momentum_pct?: number | null } | null;
  path: { key: string; label: string; kind: 'done' | 'current' | 'future' }[];
  bd_queue: { id: string; stage: string; title: string; progress_pct: number }[];
  contacts: { id: string; full_name: string | null; email: string | null; title: string | null; last_touched_at: string | null }[];
  activities: { id: string; activity_type: string; subject: string | null; body: string | null; occurred_at: string }[];
  bd_rollup: { facts_captured: number; open_asks: number; to_research: number; latest_offer: string | null };
};
export const foGetAccount = (companyId: string) => req<FoAccountRecord>(`/api/hq/founder-os/account/${companyId}`);

// @block 13.5 BD queue + briefing
export type FoQueueRow = {
  id: string;
  stage: 'verify' | 'scope' | 'define' | 'send';
  title: string;
  next_action: string | null;
  priority: 'high' | 'med' | 'low';
  progress_pct: number;
  urgency_band: 'fresh' | 'warm' | 'aging' | 'stale' | 'cold';
  urgency_label: string | null;
  age_days: number;
  owner_initials: string | null;
  owner_avatar_color: 'green' | 'purple' | 'amber' | 'blue' | 'red' | null;
  // company_id powers the company-name → /accounts/:id/bd drill-in link on
  // the kanban card. deal_stage drives the client-vs-prospect chip color
  // (won = client; everything else = prospect).
  company_id: string | null;
  badge: string | null;
  acct_name: string | null;
  deal_stage: string | null;
  // Company-level BD context counts (hq_bd.* tables filtered by company_id).
  // Shown as "12 F · 4 Fn · 3 A · 1 O" on the card.
  facts_count: number;
  findings_count: number;
  asks_count: number;
  offers_count: number;
};
export type FoQueueSummary = {
  open_count: number;
  sends_ready: number;
  high_priority: number;
  stale_count: number;
  open_value_cents: number;
};
// 2026-06-07 BD opportunity-centric rebuild. Cards = deals, columns = the
// 6 BD workflow stages stored on hq_crm.deals.bd_stage. Each opportunity
// always carries 5 modules (asks/facts/findings/pursuits/offers).
export type BdWorkflowStage =
  | 'identification' | 'processing' | 'engagement'
  | 'offer_generation' | 'negotiation'
  | 'closed_won' | 'closed_lost';

export type FoBdOpportunity = {
  deal_id: string;
  deal_name: string;
  bd_stage: BdWorkflowStage;
  pipeline_stage: string | null;
  value_cents: number | null;
  last_activity_at: string | null;
  company_id: string | null;
  badge: string | null;
  company_name: string | null;
  industry: string | null;
  facts_count: number;
  findings_count: number;
  asks_count: number;
  pursuits_count: number;
  offers_count: number;
};

export type FoBdStageRollup = {
  bd_stage: BdWorkflowStage;
  deal_count: number;
  value_cents: number;
};

export const foGetBdOpportunities = () =>
  req<{ rows: FoBdOpportunity[]; stages: FoBdStageRollup[] }>('/api/hq/founder-os/bd-opportunities');

export const foPutDealBdStage = (dealId: string, bdStage: BdWorkflowStage) =>
  req<{ id: string; bd_stage: string }>(`/api/hq/founder-os/deals/${dealId}/bd-stage`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ bdStage }),
  });

// Pursuit module — "why we should pursue + what it could lead to". Mirrors the
// findings CRUD shape.
export type BdPursuit = {
  id: string;
  dealId: string;
  companyId: string;
  category: string;
  title: string;
  body: string | null;
  upsideSummary: string | null;
  sourceSystem: string | null;
  sourceUrl: string | null;
  status: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};
type BdPursuitRow = { id: string; dealId: string; companyId: string; category: string; title: string; body: string | null; upsideSummary: string | null; sourceSystem: string | null; sourceUrl: string | null; status: string; createdBy: string | null; createdAt: string; updatedAt: string };
export const listBdPursuits = (companyId: string, status?: string) => {
  const qs = new URLSearchParams({ companyId });
  if (status) qs.set('status', status);
  return req<BdPursuitRow[]>(`/api/hq/bd/pursuits?${qs}`);
};
export const createBdPursuit = (body: {
  dealId: string; companyId: string; title: string;
  category?: string; body?: string;
  upsideSummary?: string; sourceSystem?: string; sourceUrl?: string; status?: string;
}) => req<BdPursuitRow>('/api/hq/bd/pursuits', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const foGetBdQueue = (opts?: { owner?: string; stage?: string }) => {
  const qs = new URLSearchParams();
  if (opts?.owner) qs.set('owner', opts.owner);
  if (opts?.stage) qs.set('stage', opts.stage);
  const q = qs.toString();
  return req<{ rows: FoQueueRow[]; summary: FoQueueSummary | null }>(`/api/hq/founder-os/bd-queue${q ? `?${q}` : ''}`);
};

export type FoBriefing = {
  id: string;
  stage: 'verify' | 'scope' | 'define' | 'send';
  title: string;
  urgency_label: string | null;
  priority: string;
  age_days: number;
  owner_initials: string | null;
  owner_avatar_color: string | null;
  situation: string | null;
  what_changed: string | null;
  why_it_matters: string | null;
  recommended_move: string | null;
  recommended_reason: string | null;
  evidence: string[] | null;
  actions: { kind: 'primary' | 'secondary'; label: string }[] | null;
  badge: string | null;
  acct_name: string | null;
};
export const foGetBriefing = (queueItemId: string) => req<FoBriefing>(`/api/hq/founder-os/briefing/${queueItemId}`);

// @block 13.6 Critical initiatives + VIPs + targeting
export type FoInitiative = { id: string; ord: string; title: string; status_badge: string | null; status_badge_kind: 'pos' | 'warn' | 'purple' | null; meta: string | null };
export const foGetCriticalInitiatives = () => req<FoInitiative[]>('/api/hq/founder-os/critical-initiatives');

export type FoVip = { id: string; full_name: string; initials: string; role: string | null; avatar_color: 'green' | 'amber' | 'purple' | 'blue' | 'red' | null; last_touched_at: string | null; age_band: string; age_label: string };
export const foGetVips = () => req<FoVip[]>('/api/hq/founder-os/vips');

export type FoTargeting = {
  next_move: { id: string; stage: string; title: string; next_action: string | null; priority: string; progress_pct: number; age_days: number; badge: string | null; acct_name: string | null } | null;
  also: { id: string; stage: string; title: string; next_action: string | null; priority: string; progress_pct: number; age_days: number; badge: string | null; acct_name: string | null }[];
};
export const foGetTargeting = () => req<FoTargeting>('/api/hq/founder-os/targeting');

// @block 13.7 Writes
export const foPutDealStage = (dealId: string, stage: string) =>
  req<{ id: string; stage: string; closed_at: string | null }>(
    `/api/hq/founder-os/deals/${dealId}/stage`,
    { method: 'PUT', body: JSON.stringify({ stage }) },
  );

export const foPutQueueStatus = (queueItemId: string, status: 'open' | 'done' | 'snoozed' | 'dropped', reason?: string) =>
  req<{ id: string; status: string }>(
    `/api/hq/founder-os/bd-queue/${queueItemId}/status`,
    { method: 'PUT', body: JSON.stringify({ status, reason }) },
  );

export const foPutQueueProgress = (queueItemId: string, progressPct: number) =>
  req<{ id: string; progress_pct: number }>(
    `/api/hq/founder-os/bd-queue/${queueItemId}/progress`,
    { method: 'PUT', body: JSON.stringify({ progressPct }) },
  );

export const foPostQueueAction = (queueItemId: string, actionKey: string, args?: any) =>
  req<{ id: string; action: string; ok: boolean }>(
    `/api/hq/founder-os/bd-queue/${queueItemId}/action`,
    { method: 'POST', body: JSON.stringify({ actionKey, args }) },
  );

// @section 14 Calls — Atlas /calls dialer (Google Voice deep link, general-purpose
// over serving.cold_call_lists). Reads list picker + per-list queue with prior email
// touches; writes per-dial disposition. The Voice URL is constructed client-side
// from the signed-in user's email (see Calls.tsx).

export type CallDisposition =
  | 'dialed'
  | 'connected'
  | 'voicemail'
  | 'no_answer'
  | 'bad_number'
  | 'wrong_person';

export type CallListRow = {
  list_id: string;
  list_name: string;
  description: string | null;
  generated_at: string;
  generated_by: string;
  total_items: number;
  attempted_items: number;
  last_attempt_at: string | null;
};

export type CallEmailTouch = {
  touch_n: number | null;
  subject: string | null;
  sent_at: string | null;
  sender_account: string | null;
};

export type CallQueueItem = {
  item_id: number;
  rank: number;
  lead_id: string | null;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  email: string;
  domain: string;
  firm_name: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  gmaps_rating: number | null;
  gmaps_review_count: number | null;
  html_path: string | null;
  notes: string | null;
  last_disposition: CallDisposition | null;
  last_attempt_at: string | null;
  last_called_by: string | null;
  last_callback_at: string | null;
  attempts_total: number;
  touches: CallEmailTouch[];
};

export type CallQueueMeta = {
  list_id: string;
  list_name: string;
  description: string | null;
  generated_at: string;
};

export type CallQueue = {
  list: CallQueueMeta;
  items: CallQueueItem[];
};

export type CallAttempt = {
  attempt_id: number;
  item_id: number;
  list_id: string;
  called_by_email: string;
  called_by_user_id: string | null;
  called_at: string;
  disposition: CallDisposition;
  notes: string | null;
  callback_at: string | null;
  voice_url: string | null;
};

export const listCallLists = () =>
  req<{ lists: CallListRow[] }>('/api/hq/calls/lists');

export const getCallQueue = (
  listId: string,
  filter?: 'unattempted' | 'callback_due' | 'all',
) => {
  const qs = filter && filter !== 'all' ? `?filter=${filter}` : '';
  return req<CallQueue>(`/api/hq/calls/lists/${listId}/queue${qs}`);
};

export const logCallAttempt = (body: {
  itemId: number;
  listId: string;
  disposition: CallDisposition;
  notes?: string | null;
  callbackAt?: string | null;
  voiceUrl?: string | null;
  calledAt?: string | null;
}) =>
  req<CallAttempt>('/api/hq/calls/attempts', {
    method: 'POST',
    body: JSON.stringify({
      ItemId: body.itemId,
      ListId: body.listId,
      Disposition: body.disposition,
      Notes: body.notes ?? null,
      CallbackAt: body.callbackAt ?? null,
      VoiceUrl: body.voiceUrl ?? null,
      CalledAt: body.calledAt ?? null,
    }),
  });

// @section: Call item detail (talking points / voicemail / email pre-draft)
// Detail panel revealed when an operator clicks a firm-name in /calls. Read from
// serving.cold_call_firm_enrichment + serving.cold_call_emails (touch_n=1).
export type CallEmailDraft = {
  email_artifact_id: number;
  touch_n: number | null;
  sender_account: string | null;
  subject: string | null;
  body_html: string | null;
  body_text: string | null;
  sent_at: string | null;
};

export type CallItemDetail = {
  item_id: number;
  list_id: string;
  rank: number;
  lead_id: string | null;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  email: string;
  domain: string;
  firm_name: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  html_path: string | null;
  notes: string | null;
  talking_points: string[];
  first_moves: string[];
  voicemail_script: string | null;
  email_draft: CallEmailDraft | null;
};

export type SendCallEmailResult = {
  sent_at: string;
  to: string;
  from: string;
  subject: string;
  firm_name: string | null;
  bridge_status: number;
  sent_by: string;
};

export const getCallItemDetail = (itemId: number) =>
  req<CallItemDetail>(`/api/hq/calls/items/${itemId}/detail`);

export const sendCallEmail = (itemId: number) =>
  req<SendCallEmailResult>(`/api/hq/calls/items/${itemId}/send-email`, {
    method: 'POST',
    body: JSON.stringify({ Confirm: true }),
  });

// @section: Acquisitions (Vertis Holdings CPA-acquisition pipeline)
// Backed by /api/hq/acquisitions/* (sandbox_poc.acquisitions.v_call_queue +
// v_deal_inbox_status + deal-level activities/documents).

export type AcquisitionsCallQueueItem = {
  activity_id: string | null;
  owner: string | null;
  next_action_due: string | null;
  urgency: 'overdue' | 'today' | 'soon' | 'later' | 'undated' | null;
  next_action: string | null;
  last_activity_type: string | null;
  last_outcome: string | null;
  last_summary: string | null;
  last_touched_at: string | null;
  broker_company: string | null;
  counterparty_name: string | null;
  counterparty_email: string | null;
  counterparty_phone: string | null;
  source_listing_id: string | null;
  listing_name: string | null;
  listing_state: string | null;
  asking_price: number | null;
  revenue: number | null;
  deal_stage: string | null;
  deal_id: string | null;
  broker_id: string | null;
  listing_id: string | null;
};

export type AcquisitionsCallQueue = {
  owner: string;
  count: number;
  items: AcquisitionsCallQueueItem[];
};

export type AcquisitionsDealInboxRow = {
  deal_id: string | null;
  stage: string | null;
  priority: string | null;
  next_action: string | null;
  next_action_owner: string | null;
  source_platform: string | null;
  source_listing_id: string | null;
  business_name: string | null;
  location_state: string | null;
  asking_price: number | null;
  revenue: number | null;
  broker_company: string | null;
  cim_on_disk: boolean;
  cim_count: number;
  nda_doc_count: number;
  call_count: number;
  email_count: number;
  last_outbound_call: string | null;
  last_activity_type: string | null;
  last_outcome: string | null;
  last_summary: string | null;
  last_touched_at: string | null;
  last_activity_next_action: string | null;
  last_activity_due: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type AcquisitionsDealInbox = {
  count: number;
  deals: AcquisitionsDealInboxRow[];
};

export type AcquisitionsDealActivity = {
  activity_id: string | null;
  activity_type: string | null;
  direction: string | null;
  owner: string | null;
  counterparty_name: string | null;
  counterparty_email: string | null;
  outcome: string | null;
  completed_at: string | null;
  scheduled_at: string | null;
  duration_min: number | null;
  summary: string | null;
  next_action: string | null;
  next_action_due: string | null;
  source_message_id: string | null;
  created_by: string | null;
  created_at: string | null;
};

export type AcquisitionsDealDocument = {
  document_id: string | null;
  document_type: string | null;
  document_name: string | null;
  storage_url: string | null;
  received_date: string | null;
  notes: string | null;
  created_at: string | null;
};

export type AcquisitionsDealDetail = {
  deal: AcquisitionsDealInboxRow;
  activities: AcquisitionsDealActivity[];
  documents: AcquisitionsDealDocument[];
};

export const getAcquisitionsCallQueue = (
  owner: string = 'patrick',
  urgency?: string,
) => {
  const qs = new URLSearchParams();
  qs.set('owner', owner);
  if (urgency) qs.set('urgency', urgency);
  return req<AcquisitionsCallQueue>(`/api/hq/acquisitions/call-queue?${qs}`);
};

export const getAcquisitionsDealInbox = (
  stage?: string,
  priority?: string,
) => {
  const qs = new URLSearchParams();
  if (stage) qs.set('stage', stage);
  if (priority) qs.set('priority', priority);
  const tail = qs.toString() ? `?${qs}` : '';
  return req<AcquisitionsDealInbox>(`/api/hq/acquisitions/deal-inbox${tail}`);
};

export const getAcquisitionsDealDetail = (dealId: string) =>
  req<AcquisitionsDealDetail>(`/api/hq/acquisitions/deals/${dealId}`);

// @section 15 Booking Schedules — /api/hq/booking-schedules
// Atlas-authed admin surface for nativeos.booking_schedules. Powers /booker.

export type BookingScheduleListRow = {
  slug: string;
  title: string;
  durationMinutes: number;
  timeZoneDisplay: string;
  maxDaysOut: number;
  isActive: boolean;
  url: string;
  createdAt: string;
  updatedAt: string;
};

export type BookingScheduleDetail = BookingScheduleListRow & {
  subtitle: string | null;
  footerText: string | null;
  bufferMinutes: number;
  slotIntervalMinutes: number;
  minNoticeDays: number;
  startHour: number;
  endHour: number;
  timeZoneId: string;
  availableDays: number[];
  calendarSubject: string;
  senderEmail: string;
  senderDisplayName: string;
  eventTitleTemplate: string;
  eventDescriptionTemplate: string;
  emailSubjectTemplate: string;
  reminderSubjectTemplate: string;
  intakeFields: {
    id: string;
    label: string | null;
    type: string | null;
    required: boolean;
    placeholder: string | null;
  }[];
};

export type CreateBookingScheduleBody = {
  slug: string;
  template?: string;
  title?: string;
  subtitle?: string;
  footerText?: string;
  durationMinutes?: number;
  bufferMinutes?: number;
  slotIntervalMinutes?: number;
  minNoticeDays?: number;
  maxDaysOut?: number;
  startHour?: number;
  endHour?: number;
  timeZoneId?: string;
  timeZoneDisplay?: string;
  availableDays?: number[];
  calendarSubject?: string;
  senderEmail?: string;
  senderDisplayName?: string;
  eventTitleTemplate?: string;
  eventDescriptionTemplate?: string;
  emailSubjectTemplate?: string;
  reminderSubjectTemplate?: string;
  intakeFields?: {
    id: string;
    label?: string;
    type?: string;
    required?: boolean;
    placeholder?: string;
  }[];
};

export const listBookingSchedules = (activeOnly: boolean = false) => {
  const q = activeOnly ? '?activeOnly=true' : '';
  return req<BookingScheduleListRow[]>(`/api/hq/booking-schedules${q}`);
};

export const getBookingSchedule = (slug: string) =>
  req<BookingScheduleDetail>(`/api/hq/booking-schedules/${encodeURIComponent(slug)}`);

export const createBookingSchedule = (body: CreateBookingScheduleBody) =>
  req<BookingScheduleDetail>('/api/hq/booking-schedules', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const deleteBookingSchedule = (slug: string, hard: boolean = false) =>
  req<void>(`/api/hq/booking-schedules/${encodeURIComponent(slug)}${hard ? '?hard=true' : ''}`, {
    method: 'DELETE',
  });

// @block 15.1 Documents — Atlas doc-gen + e-sign ("Act Back") over hq_docs.*
export type DocPacket = { packetId: string; name: string; useCase?: string; status: string; documentCount: number; createdAt: string };
export type DocSigner = { signerId: string; name: string; email: string; role?: string; signOrder: number; status: string; signedAt?: string; signedIp?: string };
export type DocSignatureRequest = { requestId: string; provider: string; providerRequestId?: string; isTest: boolean; status: string; createdAt: string };
export type DocListItem = {
  documentId: string; packetId: string; title: string; status: string;
  templateKey?: string; sortOrder: number; renderedAt?: string; hasPdf: boolean;
  signerCount: number; signedCount: number;
};
export type DocDetail = {
  documentId: string; packetId: string; title: string; status: string; templateKey?: string;
  entityContext: Record<string, string>; renderedAt?: string; renderedSha256?: string; hasPdf: boolean;
  signers: DocSigner[]; latestRequest?: DocSignatureRequest | null;
};
export type DocStatus = { documentId: string; status: string; request?: DocSignatureRequest | null; signers: DocSigner[] };

export const listDocumentPackets = () => req<DocPacket[]>('/api/hq/documents/packets');
export const listDocuments = (packetId?: string) =>
  req<DocListItem[]>(`/api/hq/documents${packetId ? `?packetId=${encodeURIComponent(packetId)}` : ''}`);
export const getDocument = (id: string) => req<DocDetail>(`/api/hq/documents/${id}`);
export const getDocumentStatus = (id: string) => req<DocStatus>(`/api/hq/documents/${id}/status`);
export const renderDocument = (id: string) =>
  req<{ documentId: string; sha256: string; byteSize: number; renderedAt: string }>(
    `/api/hq/documents/${id}/render`, { method: 'POST' });
export const sendDocumentToSign = (id: string, body: { isTest?: boolean; confirmBinding?: boolean }) =>
  req<{ ok: boolean; requestId: string; providerRequestId?: string; isTest: boolean }>(
    `/api/hq/documents/${id}/send-to-sign`, { method: 'POST', body: JSON.stringify(body) });

// Verbatim viewer source — the same HTML that drives the signed PDF. Returned as
// raw text so the viewer can inject it into an <iframe srcDoc> (on-screen == signed).
export const getDocumentHtml = async (id: string): Promise<string> => {
  const res = await authFetch(`${API_BASE}/api/hq/documents/${id}/html`);
  if (!res.ok) throw new Error(`HQ API ${res.status}: ${await res.text()}`);
  return res.text();
};

export const fileDocumentToDrive = (id: string) =>
  req<{ ok: boolean; executedDriveId?: string; certificateDriveId?: string; folderId?: string }>(
    `/api/hq/documents/${id}/file-to-drive`, { method: 'POST' });

// @block 15.2 Ops Pulse — deterministic daily pulse + AI review (serving.ops_pulse_*)
export type OpsPulseFlag = { severity: 'info' | 'watch' | 'alert'; area: string; note: string };
export type OpsPulseAllocation = {
  bucket: string; cash_flow_type: string; actual_pct: number; target_pct: number | null; delta_pp: number;
};
export type OpsPulseToolError = { tool: string; calls: number; errors: number; err_pct: number };
// Command/Today fields (added 2026-06-19) — the newspaper reads these off the latest day.
export type OpsPulseBusinessOutcomes = {
  meetings?: { calendar_events?: number; with_others?: number; exec_held?: number };
  deals?: { touched?: number; closed?: number; won?: number; won_value_usd?: number };
} | null;
export type OpsPulseBooking = {
  name?: string; company?: string; slug?: string;
  start_local?: string; status?: string; meet_link?: string;
};
export type OpsPulsePipelineHealth = {
  verdict?: string;
  jobs?: { job: string; hours_old: number; status: string }[];
  stale_cursors?: number;
} | null;
export type OpsPulseGitHygiene = {
  open_prs?: number; stale_prs?: number; oldest_pr_days?: number;
  sprawl_repos?: { repo: string; branches: number }[];
} | null;

export type OpsPulseDay = {
  day: string;
  sessions: number | null;
  goalRate: number | null;
  shipRate: number | null;
  recordedCostUsd: number | null;
  costByLens: Record<string, number> | null;
  allocationVsTarget: OpsPulseAllocation[] | null;
  marathonSessions: number | null;
  toolErrorsTop: OpsPulseToolError[] | null;
  reworkPairsNew: number | null;
  driftOpen: number | null;
  githubCommits: number | null;
  campaignKpis: Record<string, number> | null;
  builtAt: string;
  businessOutcomes: OpsPulseBusinessOutcomes;
  bookings: OpsPulseBooking[] | null;
  pipelineHealth: OpsPulsePipelineHealth;
  gitHygiene: OpsPulseGitHygiene;
};
export type OpsPulseReview = {
  day: string; briefMd: string; flags: OpsPulseFlag[] | null; model: string | null; writtenAt: string;
};
// Per-initiative cost rollup from analytics.v_initiative_session_cost. Includes
// the '(unattributed)' row (initiativeSlug === '(unattributed)', title null).
export type InitiativeBreakdownRow = {
  initiativeSlug: string;
  title: string | null;
  status: string | null;
  sessions: number;
  recordedCostUsd: number | null;
  hours: number | null;
  goalPct: number | null;
  marathonSessions: number;
  lastTouch: string | null;
};
export type OpsPulsePayload = {
  generatedAt: string; windowDays: number; days: OpsPulseDay[];
  review: OpsPulseReview | null;
  initiativeBreakdown: InitiativeBreakdownRow[];
};

export const getOpsPulse = (days = 28) => req<OpsPulsePayload>(`/api/hq/ops-pulse?days=${days}`);

// @block 15.3 Mission Control — six operational telemetry planes over
// /api/hq/ops/* (internal_ops + internal_outreach). Read-only panels + one
// safe act-back (POST /jobs/ack). Each GET degrades to a 503 error string when
// its keyed datasource is unwired, surfaced per-panel by the page.
export type OpsAck = { acknowledgedBy: string | null; note: string | null; acknowledgedAt: string };
export type OpsJob = {
  jobKey: string; displayName: string; ownerSystem: string | null; scheduleDesc: string | null;
  lastRunStatus: string | null; lastRunAt: string | null; detail: string | null; error: string | null;
  lastSuccessAt: string | null; verdict: string | null; latestAck: OpsAck | null;
};
export type OpsJobsPayload = { generatedAt: string; jobs: OpsJob[] };
export type OpsSessionsPayload = {
  generatedAt: string;
  counts: { sessions7d: number; sessions30d: number; cost7d: number; cost30d: number };
  scored30d: number; noship30d: number; noshipShare: number | null;
};
export type OpsDriftPayload = {
  generatedAt: string; totalUnresolved: number;
  bySeverity: { severity: string; count: number }[];
  latestRunId: number | null; latestRunAt: string | null;
};
export type OpsGithubPayload = {
  generatedAt: string; latestRunId: number | null; ingestedAt?: string | null;
  repos?: number; branches?: number; nonDefaultBranches?: number; staleBranches90d?: number;
  openPrsOver30d?: number; unprotectedDefaultBranches?: number;
};
export type OpsDeploy = {
  appName: string; success: boolean; startedAt: string; completedAt: string | null;
  slot: string | null; gitCommitShort: string | null; errorText: string | null; deployedBy: string | null;
};
export type OpsDeploysPayload = { generatedAt: string; apps: OpsDeploy[]; failures7d: number };
export type OpsCampaignsPayload = {
  generatedAt: string; latestSnapshot: string | null; campaigns: number;
  leads?: number; sent?: number; delivered?: number; replies?: number; bookings?: number;
};

export const getOpsJobs = () => req<OpsJobsPayload>('/api/hq/ops/jobs');
export const ackOpsJob = (jobKey: string, note?: string) =>
  req<{ ok: boolean; ackId: string; acknowledgedAt: string }>('/api/hq/ops/jobs/ack',
    { method: 'POST', body: JSON.stringify({ jobKey, note }) });
export const getOpsSessions = () => req<OpsSessionsPayload>('/api/hq/ops/sessions');
export const getOpsDrift = () => req<OpsDriftPayload>('/api/hq/ops/drift');
export const getOpsGithub = () => req<OpsGithubPayload>('/api/hq/ops/github');
export const getOpsDeploys = () => req<OpsDeploysPayload>('/api/hq/ops/deploys');
export const getOpsCampaigns = () => req<OpsCampaignsPayload>('/api/hq/ops/campaigns');

// @block 15.4 Initiatives — editable register (exec_team.initiatives) joined to
// its cost rollup, the weekly AI proposal queue (serving.initiative_proposals,
// degraded to [] if the table doesn't exist yet), and the per-session manual
// attribution override (enrichment.session_initiative).
export type InitiativeRow = {
  slug: string;
  title: string;
  priorityRank: number | null;
  priorityRankText: string | null;
  status: string;            // open | committed | paused | shipped | killed
  ownerIsAdam: boolean;
  descriptionMd: string | null;
  nextAction: string | null;
  bodyMd: string | null;
  createdAt: string;
  updatedAt: string;
  // Cost rollup (null/0 when nothing is attributed to this initiative yet).
  sessions: number;
  recordedCostUsd: number | null;
  hours: number | null;
  goalPct: number | null;
  avgFrustration: number | null;
  marathonSessions: number;
  lastTouch: string | null;
};

// serving.initiative_proposals is shaped by the weekly curator chip and may not
// exist yet; rows are passed through as free-form records (schema-agnostic).
export type InitiativeProposal = Record<string, unknown>;

export type UpsertInitiativeBody = {
  slug: string;
  title?: string;
  priorityRank?: number | null;
  status?: string;
  nextAction?: string | null;
  descriptionMd?: string | null;
  bodyMd?: string | null;
  ownerIsAdam?: boolean;
};

export const getInitiatives = () => req<InitiativeRow[]>('/api/hq/initiatives');

export const getInitiativeProposals = () =>
  req<InitiativeProposal[]>('/api/hq/initiative-proposals');

export const upsertInitiative = (body: UpsertInitiativeBody) =>
  req<InitiativeRow>('/api/hq/initiatives', { method: 'POST', body: JSON.stringify(body) });

// Manual per-session override. Pass slug=null to deliberately un-attribute a session.
export const setSessionInitiative = (sessionId: string, initiativeSlug: string | null) =>
  req<{ sessionId: string; initiativeSlug: string | null; source: string; confidence: number; assignedAt: string }>(
    `/api/hq/sessions/${encodeURIComponent(sessionId)}/initiative`,
    { method: 'POST', body: JSON.stringify({ initiativeSlug }) });

// @block 15.5 BD Targets — Atlas /targets surface (hq_bd.target_firms).
// Shared BD-team view; voice-captured firm/vertical/business model. Daniel Diaz et al.
export type TargetStatus = 'identified' | 'demo_ready' | 'shown' | 'next_step';

export type TargetFirm = {
  id: string;
  firmName: string;
  vertical?: string | null;
  businessModel?: string | null;
  relationshipSource?: string | null;
  status: TargetStatus;
  ownerEmail?: string | null;
  companyId?: string | null;
  voiceTranscript?: string | null;
  demoNote?: string | null;
  notes?: string | null;
  capturedAt: string;
  createdAt: string;
  updatedAt: string;
};

export const listTargets = (opts?: { status?: string; vertical?: string }) => {
  const q = new URLSearchParams();
  if (opts?.status) q.set('status', opts.status);
  if (opts?.vertical) q.set('vertical', opts.vertical);
  const qs = q.toString();
  return req<TargetFirm[]>(`/api/hq/bd-targets${qs ? `?${qs}` : ''}`);
};

export const getTarget = (id: string) => req<TargetFirm>(`/api/hq/bd-targets/${id}`);

export const createTarget = (body: {
  firmName: string;
  vertical?: string;
  businessModel?: string;
  relationshipSource?: string;
  voiceTranscript?: string;
  notes?: string;
}) => req<TargetFirm>('/api/hq/bd-targets', { method: 'POST', body: JSON.stringify(body) });

export const patchTarget = (
  id: string,
  body: Partial<{
    firmName: string;
    vertical: string;
    businessModel: string;
    relationshipSource: string;
    status: TargetStatus;
    demoNote: string;
    notes: string;
  }>,
) => req<TargetFirm>(`/api/hq/bd-targets/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

// @block 15.6 Captures - prospect-conveyor capture inbox (/card-capture surface).
// Business-card photos + paired voice notes over the generalized
// hq_core.voice_captures, scoped to hq_crm.events. Binary uploads go multipart
// (uploadForm below - Authorization only, browser sets the multipart boundary;
// authFetch's forced JSON Content-Type would corrupt FormData bodies).

export type HqEvent = {
  id: string;
  name: string;
  eventDate?: string | null;
  venue?: string | null;
  organizer?: string | null;
  eventType?: string | null;
  notes?: string | null;
  captureCount: number;
};

export type CardFields = {
  firstName?: string | null;
  lastName?: string | null;
  title?: string | null;
  companyName?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  notes?: string | null;
};

export type HqCapture = {
  id: string;
  kind: 'card_image' | 'voice';
  status: string; // pending | classified | completed
  classification?: string | null;
  eventId?: string | null;
  eventName?: string | null;
  pairedCaptureId?: string | null;
  imageDriveId?: string | null;
  audioDriveId?: string | null;
  durationMs?: number | null;
  ocrText?: string | null;
  transcript?: string | null;
  error?: string | null;
  targetType?: string | null;
  targetId?: string | null;
  capturedAt: string;
  completedAt?: string | null;
};

export const listEvents = () => req<HqEvent[]>('/api/hq/crm/events');
export const createEvent = (body: {
  name: string;
  eventDate?: string;
  venue?: string;
  organizer?: string;
  eventType?: string;
  notes?: string;
}) => req<HqEvent>('/api/hq/crm/events', { method: 'POST', body: JSON.stringify(body) });

export const listCaptures = (opts?: { status?: string; eventId?: string; kind?: string }) => {
  const q = new URLSearchParams();
  if (opts?.status) q.set('status', opts.status);
  if (opts?.eventId) q.set('eventId', opts.eventId);
  if (opts?.kind) q.set('kind', opts.kind);
  const qs = q.toString();
  return req<HqCapture[]>(`/api/hq/captures${qs ? `?${qs}` : ''}`);
};

export const getCapture = (id: string) =>
  req<{ capture: HqCapture; paired: HqCapture | null }>(`/api/hq/captures/${id}`);

export const parseCapture = (id: string) =>
  req<{ ocrText?: string | null; fields?: CardFields | null }>(
    `/api/hq/captures/${id}/parse`, { method: 'POST' });

export const confirmCapture = (
  id: string,
  body: {
    company: { id?: string; name: string; domain?: string; industry?: string };
    contact: {
      id?: string; firstName?: string; lastName?: string;
      email?: string; phone?: string; title?: string; linkedinUrl?: string;
    };
    enqueueChecklist: boolean;
    meetingAt?: string; // ISO; drives kickoff_mode auto_meeting + due_by
    notes?: string;
  },
) => req<{ ok: boolean; companyId?: string; contactId: string; checklistRunId?: string | null; checklistNote?: string | null }>(
  `/api/hq/captures/${id}/confirm`, { method: 'POST', body: JSON.stringify(body) });

// @section 12 VPG Reply Review — the operator queue for open GoHighLevel replies.
// Backed by /api/hq/vpg-replies (deals.v_reply_review_queue + deals.reply_candidate on
// optiqos_prod). executeVpgReply is the ONLY call that transmits an SMS, and it only ever
// fires from a human clicking Execute.

export type VpgQueueRow = {
  locationId: string;
  contactId: string;
  market?: string | null;
  persona?: string | null;
  kind?: string | null;
  classification?: string | null;
  replyState?: string | null;
  lastInboundId?: string | null;
  lastInboundAt?: string | null;
  lastInboundBody?: string | null;
  lastOutboundAt?: string | null;
  inboundN: number;
  outboundN: number;
  minutesWaiting?: number | null;
  contactName?: string | null;
  firstName?: string | null;
  phone?: string | null;
  candidateN: number;
  replyDraft?: string | null;
  /** Quiet for 28+ days. Derived from last_inbound_at, so it reverses itself on a new reply. */
  isDormant?: boolean;
};

export type VpgMessage = {
  id?: string | null;
  direction?: string | null;
  body?: string | null;
  dateAdded?: string | null;
  status?: string | null;
};

export type VpgCandidate = {
  variant: string;
  body?: string | null;
  /** Second half of a double text. Null = single message. */
  body2?: string | null;
  modelVersion?: string | null;
  forInboundId?: string | null;
  generatedAt?: string | null;
  /** True when the candidate answers an OLDER inbound than the thread's newest. */
  stale: boolean;
};

export type VpgDecision = {
  decidedAt?: string | null;
  decidedBy?: string | null;
  action?: string | null;
  chosenVariant?: string | null;
  customized: boolean;
  rejectReason?: string | null;
};

export type VpgThreadDetail = {
  thread: VpgQueueRow & {
    note?: string | null; email?: string | null; dnd?: boolean;
    repliedAt?: string | null; reviewedBy?: string | null;
  };
  /** False for opt-out / DND threads: readable, never sendable. */
  sendable: boolean;
  messages: VpgMessage[];
  candidates: VpgCandidate[];
  decisions: VpgDecision[];
};

export type VpgFacets = {
  total: number;
  facets: Record<string, { value: string | null; count: number }[]>;
};

export type VpgScoreRow = {
  variant: string;
  decisions: number;
  sent: number;
  sentClean: number;
  sentEdited: number;
  rejected: number;
  failed: number;
  cleanShipPct?: number | null;
  lastDecidedAt?: string | null;
};

export const listVpgQueue = (params?: {
  persona?: string; kind?: string; market?: string; classification?: string;
  q?: string; ready?: boolean; dormant?: boolean; sort?: 'oldest' | 'newest'; take?: number;
}) => {
  const qs = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  const s = qs.toString();
  return req<VpgQueueRow[]>(`/api/hq/vpg-replies/queue${s ? `?${s}` : ''}`);
};

// @block 12.1 All Threads — the browsing surface. Every thread, any contact, any location,
// regardless of reply state. Read-only; sending only ever happens in Reply Review.
export type VpgAllThreadRow = VpgQueueRow & {
  awaitingReply: boolean;
  email?: string | null;
  dnd?: boolean;
};

export const listVpgAllThreads = (params?: {
  persona?: string; kind?: string; market?: string; classification?: string;
  replyState?: string; q?: string; sort?: 'recent' | 'oldest' | 'busiest';
  take?: number; skip?: number;
}) => {
  const qs = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  const s = qs.toString();
  return req<{ total: number; skip: number; take: number; rows: VpgAllThreadRow[] }>(
    `/api/hq/vpg-replies/all-threads${s ? `?${s}` : ''}`);
};

export const getVpgAllFacets = () => req<VpgFacets>('/api/hq/vpg-replies/all-facets');

// @block 12.2 Replies that never reached the agent. reply_state='sent' only means GHL
// accepted the message; ~1% of outbound SMS never land, and those threads leave the queue
// either way, so without this they are invisible.
export type VpgDeliveryIssue = {
  locationId: string;
  contactId: string;
  contactName?: string | null;
  phone?: string | null;
  market?: string | null;
  persona?: string | null;
  decidedAt?: string | null;
  decidedBy?: string | null;
  chosenVariant?: string | null;
  /** 'undelivered' | 'failed' | 'stalled' */
  issue: string;
  status?: string | null;
  sentBody?: string | null;
  sentBody2?: string | null;
  messageId?: string | null;
  messageAt?: string | null;
  replyState?: string | null;
  /** The carrier's stated reason, e.g. "Error 30006 - Landline ... Cannot receive SMS." */
  carrierError?: string | null;
};

export const listVpgDeliveryIssues = (params?: { days?: number; take?: number }) => {
  const qs = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) qs.set(k, String(v));
  });
  const s = qs.toString();
  return req<VpgDeliveryIssue[]>(`/api/hq/vpg-replies/delivery-issues${s ? `?${s}` : ''}`);
};

/** Ask for recommendations on a thread that has none. Idempotent; the worker drains it. */
export const requestVpgDraft = (locationId: string, contactId: string) =>
  req<{ state: 'ready' | 'queued' | 'already_queued'; candidates?: number }>(
    `/api/hq/vpg-replies/threads/${encodeURIComponent(locationId)}/${encodeURIComponent(contactId)}/draft`,
    { method: 'POST' });

export const getVpgFacets = () => req<VpgFacets>('/api/hq/vpg-replies/facets');
export const getVpgScoreboard = () => req<VpgScoreRow[]>('/api/hq/vpg-replies/scoreboard');

export const getVpgThread = (locationId: string, contactId: string) =>
  req<VpgThreadDetail>(
    `/api/hq/vpg-replies/threads/${encodeURIComponent(locationId)}/${encodeURIComponent(contactId)}`);

/** Sends. Human click only. `variant` is null when the operator wrote from scratch. */
export const executeVpgReply = (
  locationId: string, contactId: string,
  body: { variant: string | null; message: string; message2?: string | null },
) => req<{
  ok: boolean; state: string; messageIds: string[];
  customized: boolean; variant?: string | null; warning?: string | null;
}>(
  `/api/hq/vpg-replies/threads/${encodeURIComponent(locationId)}/${encodeURIComponent(contactId)}/execute`,
  { method: 'POST', body: JSON.stringify(body) });

export const rejectVpgReply = (
  locationId: string, contactId: string,
  body: { state: 'handled_by_human' | 'dead'; reason?: string },
) => req<{ ok: boolean; state: string }>(
  `/api/hq/vpg-replies/threads/${encodeURIComponent(locationId)}/${encodeURIComponent(contactId)}/reject`,
  { method: 'POST', body: JSON.stringify(body) });

// Multipart POST: Authorization header only - no Content-Type so the browser
// sets the multipart boundary itself.
async function uploadForm<T>(path: string, form: FormData): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HQ API ${res.status}: ${text || res.statusText}`);
  }
  return (await res.json()) as T;
}

export const uploadCardCapture = (
  file: File,
  opts?: { eventId?: string; pairedCaptureId?: string; ocrText?: string },
): Promise<{ captureId: string; driveId: string; ocrText?: string | null; ocrError?: string | null }> => {
  const form = new FormData();
  form.set('file', file);
  if (opts?.eventId) form.set('eventId', opts.eventId);
  if (opts?.pairedCaptureId) form.set('pairedCaptureId', opts.pairedCaptureId);
  // On-device Tesseract text (primary OCR). Persisted to voice_captures.ocr_text
  // so the research worker + resume flow have it without re-running OCR.
  if (opts?.ocrText) form.set('ocrText', opts.ocrText);
  return uploadForm('/api/hq/captures/card', form);
};

export const uploadVoiceCapture = (opts: {
  blob?: Blob;
  transcript?: string;
  durationMs?: number;
  eventId?: string;
  pairedCaptureId?: string;
}): Promise<{ captureId: string; driveId?: string | null }> => {
  const form = new FormData();
  if (opts.blob) form.set('file', opts.blob, 'note.webm');
  if (opts.transcript) form.set('transcript', opts.transcript);
  if (opts.durationMs != null) form.set('durationMs', String(Math.round(opts.durationMs)));
  if (opts.eventId) form.set('eventId', opts.eventId);
  if (opts.pairedCaptureId) form.set('pairedCaptureId', opts.pairedCaptureId);
  return uploadForm('/api/hq/captures/voice', form);
};
